import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@mlm/ui';
import {
  api,
  getAccountingOverview,
  getAdminUser,
  getAssistantUsage,
  getTenantFeatures,
  listCompPlanConfigs,
  listMemberships,
  listPaymentTerms,
  listVoucherPrograms,
  patchTenantFeatures,
  type AdminUserSearchResult,
  type AssistantUsageSummary,
  type CompPlanConfigRow,
  type Membership,
  type TenantFeatures,
} from '../lib/api';
import { MemberPicker } from '../components/member-picker';
import { SlotCodePicker } from '../components/slot-code-picker';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import { RepeatableRows } from '../components/repeatable-rows';
import { FreeSlotBackfill } from '../components/settings/free-slot-backfill';
import { AssistantPersonaEditor } from '../components/assistant-persona-editor';
import {
  EARNINGS_BUCKETS,
  EARNINGS_SUB_BUCKETS,
  LEDGER_KIND_GROUPS,
  LEDGER_KIND_LABEL_DEFAULTS,
  LEDGER_SOURCE_PLAN_CODES,
  GARNISH_EXTRA_KINDS,
  LEDGER_SOURCE_PLAN_LABEL_DEFAULTS,
  CORE_NOTIFICATION_EVENT_CODES,
  earningsBreakdownRowLabel,
  resolveEarningsBreakdownRows,
  resolveTopupBonuses,
  resolveTopupVoucherPrograms,
  Decimal,
  type EarningsBreakdownRow,
  type EarningsBreakdownRowMode,
  type RegistrationField,
  type KycRequirementDef,
  type WalletTopupBracket,
  hasBespokeLanding,
  AR_PERMS,
} from '@mlm/shared';
import {
  EventTemplateEditor,
  MerchantRequirementsEditor,
  Toggle,
} from '../components/settings/shared-fields';
import { hasPerm, hasRole } from '../lib/auth-store';
import {
  planDisplayName,
  planTrigger,
  SUPPORTED_PLAN_CODES,
  TRIGGER_LABEL,
  TRIGGER_ORDER,
} from '../components/compplan';

/**
 * Whether THIS build's tenant ships its own landing page at `/` (baked
 * VITE_TENANT, same mechanism the sidebar + storefront manifests use).
 *
 * Gates the "Information site only" toggle: with the storefront off, a tenant
 * without a bespoke landing would serve a blank public site. The API enforces
 * the same rule authoritatively on `PATCH /admin/tenant/features`.
 */
const TENANT_HAS_BESPOKE_LANDING = hasBespokeLanding(
  import.meta.env.VITE_TENANT as string | undefined,
);

// Superseded by the per-product "Non-activated members" price (product Discount
// tab). The tenant-level membership alias still applies as a fallback where
// set — flip to restore the picker.
const SHOW_TENANT_NON_ACTIVATED_PICKER = false;

/**
 * Tenant feature toggles. Read by any admin; PATCH is super_admin-only via
 * the `tenant.features.write` permission gate on the route side. UI hides
 * the save button for non-super_admin.
 */
export function SettingsPage() {
  const queryClient = useQueryClient();
  const featuresQuery = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
  });
  const [draft, setDraft] = useState<TenantFeatures | null>(null);
  const [saved, setSaved] = useState(false);
  // Raw text for the admin-email recipients textarea. A join('\n')/split
  // round-trip through the draft array would swallow the separator as it is
  // typed (a trailing comma/newline parses to nothing and re-renders away), so
  // the textarea displays this raw buffer and only the PARSED list lives in
  // the draft. null = not yet edited; display falls back to the stored list.
  const [emailRecipientsText, setEmailRecipientsText] = useState<string | null>(null);
  useEffect(() => {
    if (featuresQuery.data && !draft) setDraft(featuresQuery.data);
  }, [featuresQuery.data, draft]);

  const compPlanConfigsQuery = useQuery({
    queryKey: ['admin-compplan-configs'],
    queryFn: listCompPlanConfigs,
  });
  // Active tiers, for the non-activated pricing picker below. A tier archived
  // after being picked won't appear here — the picker renders the saved id as
  // an explicit "not applied" row rather than silently blanking the setting.
  const membershipsQuery = useQuery({
    queryKey: ['memberships'],
    queryFn: listMemberships,
  });
  // The tiers a free slot can be made at, for the Free slot at signup picker.
  const fsEligibleMemberships = useMemo(
    () => (membershipsQuery.data ?? []).filter((m) => m.config?.fs_eligible === true),
    [membershipsQuery.data],
  );
  // Gift check programs a top-up bracket can give. The route answers only while
  // gift checks are on (saved), so a just-ticked switch shows an empty list.
  const voucherProgramsQuery = useQuery({
    queryKey: ['voucher-programs'],
    queryFn: listVoucherPrograms,
    enabled: featuresQuery.data?.vouchers_enabled === true,
    retry: false,
  });
  const topupPrograms = useMemo(
    () =>
      (voucherProgramsQuery.data ?? [])
        .filter((p) => p.earnKind === 'topup' && p.active)
        .map((p) => ({ id: p.id, name: p.name })),
    [voucherProgramsQuery.data],
  );
  const latestConfigByPlan = useMemo(() => {
    const map = new Map<string, CompPlanConfigRow>();
    for (const row of compPlanConfigsQuery.data ?? []) {
      const existing = map.get(row.planCode);
      if (!existing || row.version > existing.version) map.set(row.planCode, row);
    }
    return map;
  }, [compPlanConfigsQuery.data]);

  // MTD assistant spend for the Assistant card. Super-admin-only endpoint —
  // gate on the same check that shows the save button to avoid 403 noise.
  const assistantUsageQuery = useQuery({
    queryKey: ['assistant-usage'],
    queryFn: getAssistantUsage,
    enabled: hasRole('super_admin'),
  });

  // The GL posting start pins into gl_poster_state on the first enabled tick
  // and the poster never reads the flag again — so the field below has to show
  // the PIN, not the flag, or it lies for the rest of the tenant's life.
  const postingStartQ = useQuery({
    queryKey: ['accounting-overview'],
    queryFn: getAccountingOverview,
    enabled: featuresQuery.data?.accounting_enabled === true,
  });
  const postingStartPinned = postingStartQ.data?.postingStart.pinned === true;

  // The default-terms picker offers the real payment-terms list (managed under
  // Accounting → Customers → Payment terms). Reading it needs `customer.read`;
  // without that the field falls back to the plain code box.
  const paymentTermsQ = useQuery({
    queryKey: ['payment-terms', 'all'],
    queryFn: () => listPaymentTerms(true),
    enabled: hasPerm(AR_PERMS.customerRead),
  });

  const patch = useMutation({
    mutationFn: patchTenantFeatures,
    meta: { successMessage: 'Settings saved.' },
    onSuccess: (data) => {
      setDraft(data);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['tenant-features'] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (!draft) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const canWrite = hasRole('super_admin');

  // Build the PATCH payload from the draft, bumping legal-doc versions when text
  // changed. Used for BOTH the confirm-dialog diff (`next`) and the mutation, so
  // the preview matches exactly what gets saved.
  function buildPayload(): TenantFeatures {
    const server = featuresQuery.data;
    const payload: TenantFeatures = { ...(draft as TenantFeatures) };
    // Two coaching tables are owned by the Coaching page (Features and
    // Credits tabs) and merged one level deep on the server: carrying a
    // stale copy in this full-object save would overwrite an edit made there
    // since this page loaded. Leave them out; the server keeps what it has.
    if (payload.coaching) {
      const { plan_features: _pf, credit_costs: _cc, ...rest } = payload.coaching;
      payload.coaching = rest as TenantFeatures['coaching'];
    }
    // Publish a new Terms/Privacy version when the text changed. The server
    // also guards this, but the full-object PATCH would otherwise carry the
    // old version forward and suppress the bump.
    if (server) {
      if (payload.terms_text !== server.terms_text) {
        payload.terms_version = server.terms_version + 1;
      }
      if (payload.privacy_policy_text !== server.privacy_policy_text) {
        payload.privacy_policy_version = server.privacy_policy_version + 1;
      }
      if (payload.activation_terms_text !== server.activation_terms_text) {
        payload.activation_terms_version = server.activation_terms_version + 1;
      }
    }
    // House VAT rate rides the FULL-object PATCH, and its input can hold a
    // mid-edit value ('', '12.', '-5', >4dp) — or be unmounted with one after
    // the toggle flips off — which would 400 the ENTIRE save. Normalize any
    // value the server's moneyStringSchema would reject back to the stored
    // value (or the '12' default) instead of blocking every other setting.
    if (!/^\d+(\.\d{1,4})?$/.test(payload.house_vat_default_pct ?? '')) {
      payload.house_vat_default_pct = server?.house_vat_default_pct ?? '12';
    }
    // Same trap for the GL posting start date: a mid-edit/partial value would
    // fail the server's YYYY-MM-DD regex and 400 the ENTIRE save. Reset any
    // malformed value back to the stored one (null = no bound).
    if (
      payload.accounting_posting_start_date != null &&
      !/^\d{4}-\d{2}-\d{2}$/.test(payload.accounting_posting_start_date)
    ) {
      payload.accounting_posting_start_date = server?.accounting_posting_start_date ?? null;
    }
    // A half-typed PIN amount ('12.', '-5', '1.234') would fail the server's
    // money schema and 400 the whole save: fall back to the stored value.
    if (
      payload.pos_topup_pin_above != null &&
      !/^\d+(\.\d{1,4})?$/.test(payload.pos_topup_pin_above)
    ) {
      payload.pos_topup_pin_above = server?.pos_topup_pin_above ?? null;
    }
    // A blank gift check name would fail the server's min(1) and 400 the whole
    // save: fall back to the stored name.
    if (!payload.voucher_label?.trim()) {
      payload.voucher_label = server?.voucher_label || 'Gift Check';
    }
    return payload;
  }

  const coinLabel = draft.coin_label || 'Coins';
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Tenant Settings</h1>
        {saved && <span className="text-xs text-success">Saved</span>}
      </div>

      <div className="flex gap-6">
        <aside className="hidden lg:block w-52 shrink-0">
          <nav className="sticky top-20 space-y-1 text-sm">
            <a
              href="#kits"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Membership kits
            </a>
            <a
              href="#security"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Member security
            </a>
            <a
              href="#kyc"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              KYC &amp; cash-out
            </a>
            <a
              href="#sponsor"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Default sponsor
            </a>
            <a
              href="#registration-fields"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Registration fields
            </a>
            <a
              href="#profile-editing"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Profile editing
            </a>
            <a
              href="#terms"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Terms &amp; Privacy
            </a>
            <a
              href="#activation"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Activation &amp; Registration
            </a>
            <a
              href="#signup-free-slot"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Free slot at signup
            </a>
            <a
              href="#placement"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Slot placement
            </a>
            <a
              href="#genealogy-details"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Genealogy box details
            </a>
            <a
              href="#volume-pv"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Volume &amp; PV
            </a>
            <a
              href="#stairstep"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Stairstep ranks
            </a>
            <a
              href="#cd-slots"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              CD / Half-CD slots
            </a>
            <a
              href="#pricing"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing &amp; fulfillment
            </a>
            <a
              href="#storefront-home"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Storefront home
            </a>
            <a
              href="#merchants"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Merchants
            </a>
            <a
              href="#compplans"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Comp plans
            </a>
            <a
              href="#labels"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Ledger labels
            </a>
            <a
              href="#earnings-order"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Earnings breakdown
            </a>
            <a
              href="#wallet-topup"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Wallet top-up
            </a>
            <a
              href="#autoship"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Auto-Ship
            </a>
            <a
              href="#gift-checks"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Gift checks
            </a>
            <a
              href="#member-resources"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Resources library
            </a>
            {(canWrite || draft.lms_enabled) && (
              <a
                href="#learning"
                className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                Learning
              </a>
            )}
            {(canWrite || draft.coaching_enabled) && (
              <a
                href="#coaching"
                className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                Coaching
              </a>
            )}
            <a
              href="#member-subscription"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Member subscription
            </a>
            <a
              href="#notifications"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Notifications
            </a>
            <a
              href="#license"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              License / Domain
            </a>
            <a
              href="#receivables"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              Receivables
            </a>
            <a
              href="#system"
              className="block px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              System
            </a>
          </nav>
        </aside>

        <div className="flex-1 min-w-0 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* R2 — its own card rather than another row on the already-huge
                System card: these are day-to-day bookkeeping settings, not
                super-admin plumbing. */}
            <Card id="receivables" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Receivables</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="General invoicing"
                  description="Bill customers who are not consignees — individuals and businesses, not just members. Turning this off hides the surfaces; documents already posted stay in the ledger."
                  value={draft.ar_invoicing_enabled === true}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, ar_invoicing_enabled: v })}
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium">Default payment terms</div>
                  <p className="text-xs text-muted-foreground">
                    Used when neither the document nor the customer names one. Manage the list under
                    Accounting → Customers → Payment terms.
                  </p>
                  {paymentTermsQ.data ? (
                    <select
                      className="h-9 w-full max-w-xs rounded-md border border-input bg-background px-2 text-sm shadow-sm"
                      value={draft.ar_default_terms_code ?? 'due_on_receipt'}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({ ...draft, ar_default_terms_code: e.target.value })
                      }
                    >
                      {/* A default pointing at a term that was since switched off
                          still resolves server-side; keep it visible, not blank. */}
                      {paymentTermsQ.data.some(
                        (t) => t.code === (draft.ar_default_terms_code ?? 'due_on_receipt'),
                      ) ? null : (
                        <option value={draft.ar_default_terms_code ?? 'due_on_receipt'}>
                          {draft.ar_default_terms_code ?? 'due_on_receipt'} (not in the list)
                        </option>
                      )}
                      {paymentTermsQ.data.map((t) => (
                        <option key={t.code} value={t.code}>
                          {t.name}
                          {t.active ? '' : ' (switched off)'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <Input
                        value={draft.ar_default_terms_code ?? 'due_on_receipt'}
                        disabled={!canWrite}
                        onChange={(e) =>
                          setDraft({ ...draft, ar_default_terms_code: e.target.value })
                        }
                        className="max-w-xs font-mono"
                      />
                      <p className="text-xs text-muted-foreground">
                        Term code (the list could not be loaded with your access).
                      </p>
                    </>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="text-sm font-medium">Credit limit</div>
                  <p className="text-xs text-muted-foreground">
                    What happens when posting a billing would take a customer past their limit.
                    Checked when the document is posted, never while it is a draft.
                  </p>
                  <select
                    className="h-9 w-full max-w-xs rounded-md border border-input bg-background px-2 text-sm shadow-sm"
                    value={draft.ar_credit_limit_mode ?? 'warn'}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        ar_credit_limit_mode: e.target.value as 'off' | 'warn' | 'block',
                      })
                    }
                  >
                    <option value="off">Off — never check</option>
                    <option value="warn">Warn — post anyway, flag it</option>
                    <option value="block">Block — refuse the post</option>
                  </select>
                </div>
              </CardContent>
            </Card>
            <Card id="kits" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Membership kits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Kit codes are transferable (gift-card mode)"
                  description="When on, anyone with the Code+PIN can redeem. When off, only the original buyer can redeem their own codes. Existing codes keep their issuance-time setting."
                  value={draft.kit_codes_transferable}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, kit_codes_transferable: v })}
                />
                <Toggle
                  label="Allow downline code transfers"
                  description="When on, a member can transfer an unredeemed, transferable code from their Code Vault to a member in their downline (matched by username + email). The code moves out of the sender's vault into the recipient's."
                  value={draft.kit_codes_downline_transfer}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, kit_codes_downline_transfer: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="expire">
                    Kit code expiry (days)
                  </label>
                  <Input
                    id="expire"
                    type="number"
                    min={0}
                    max={36500}
                    value={draft.kit_codes_expire_days ?? ''}
                    placeholder="Never expires"
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        kit_codes_expire_days:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Blank = never expires. Applies to NEW codes only; existing codes keep their
                    original expiry.
                  </p>
                </div>
                <Toggle
                  label="Auto-create slot on kit purchase — Ecommerce"
                  description="When on, buying a Membership Kit through the online store creates and activates the slot automatically (sponsor + placement resolved from registration), instead of leaving a Code+PIN for the member to redeem from their Vault. Upgrade-only codes and any past the member's slot cap still wait in the Vault."
                  value={draft.kit_auto_create_slot.ecom}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      kit_auto_create_slot: { ...draft.kit_auto_create_slot, ecom: v },
                    })
                  }
                />
                <Toggle
                  label="Auto-create slot on kit purchase — Cashier (POS)"
                  description="Same as above, but for kits sold at the cashier. For manual-release branches the slot is created at hand-over (release), not at checkout."
                  value={draft.kit_auto_create_slot.cashier}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      kit_auto_create_slot: { ...draft.kit_auto_create_slot, cashier: v },
                    })
                  }
                />
                <Toggle
                  label="Auto-create slot on kit purchase — Virtual kits"
                  description="Same again, but for an order containing only VIRTUAL items — whichever channel sold it. A virtual kit has nothing to hand over and already completes the moment it's paid, so sending the buyer to hunt a Code+PIN in their Vault is a step that only makes sense for a kit someone physically gives them. This ADDS to the two settings above: turning it on never switches off a channel that already auto-creates."
                  value={draft.kit_auto_create_slot.virtual}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      kit_auto_create_slot: { ...draft.kit_auto_create_slot, virtual: v },
                    })
                  }
                />
              </CardContent>
            </Card>

            <Card id="security" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Member security</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Require 2FA for members"
                  description="When on, members must enroll TOTP before they can complete login. Admin and cashier always require 2FA regardless of this flag."
                  value={draft.member_2fa_required}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, member_2fa_required: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">When an account is archived</label>
                  <Select
                    value={draft.account_archive_mode ?? 'deactivate'}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({ ...draft, account_archive_mode: v as 'deactivate' | 'erase' })
                    }
                  >
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deactivate">
                        Close it — reversible (recommended)
                      </SelectItem>
                      <SelectItem value="erase">
                        Erase the person&apos;s details — permanent
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    <strong>Close</strong> sets the account to archived and signs the member out
                    everywhere. Nothing is deleted, and either the member (by signing in and
                    choosing Reactivate) or an administrator can reopen it. <strong>Erase</strong>{' '}
                    also overwrites their name, email, phone, address, KYC and payout details, and
                    queues their uploaded files for deletion — it cannot be undone, and the account
                    can never be reopened. In both cases orders, the ledger, certificates and the
                    member&apos;s place in the genealogy tree are kept.
                  </p>
                </div>
                <Toggle
                  label="Block accounts after a period of inactivity"
                  description="When on, a member who goes the number of days below with no new direct and no repeat purchase is blocked automatically: they can no longer log in, and the login screen tells them why. Staff and company accounts are never blocked. Unblock from the member's page; that restarts their clock. Commissions still land in their wallet while blocked."
                  value={draft.inactivity_block_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, inactivity_block_enabled: v })}
                />
                {draft.inactivity_block_enabled && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="inactivity-days">
                      Days without a direct or repeat purchase
                    </label>
                    <Input
                      id="inactivity-days"
                      type="number"
                      min={30}
                      max={3650}
                      className="max-w-[10rem]"
                      value={draft.inactivity_block_days}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          inactivity_block_days: Number(e.target.value),
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {draft.inactivity_block_since
                        ? `Counting from ${new Date(draft.inactivity_block_since).toLocaleDateString()} at the earliest, the day this was switched on, so nobody was blocked for time before it.`
                        : 'Nobody is blocked for time before the day you switch this on: every clock starts no earlier than that.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card id="license" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>License / Domain expiration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <label className="text-sm font-medium" htmlFor="domain-expiry">
                  Expiration date
                </label>
                <Input
                  id="domain-expiry"
                  type="date"
                  value={draft.domain_expires_at ?? ''}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      domain_expires_at: e.target.value === '' ? null : e.target.value,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {canWrite
                    ? 'Super-admin only. The admin gets a renewal-reminder popup on login starting 3 months before this date. Blank = no reminder. Notification-only — it does not lock the tenant out.'
                    : 'Set by the platform operator. A renewal reminder appears on login within 3 months of this date.'}
                </p>
              </CardContent>
            </Card>

            <Card id="kyc" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>KYC &amp; cash-out verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Require verified KYC for cash-out"
                  description="When on, a member must be KYC-verified before they can request a cash-out (the server rejects payout requests otherwise). Verify a member from their member-detail page. When off, cash-out skips the KYC gate entirely."
                  value={draft.cashout_require_kyc}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cashout_require_kyc: v })}
                />
                <Toggle
                  label="Lock cash-out name to account owner"
                  description="When on, the account-holder name on a cash-out is forced to the member's own Full Name — the member can't type a different name (guards against cashing out under someone else's name after account access is shared). Tag a payout-method field as 'Account holder name' in the Fees & Tax → payout methods editor for this to apply. When off, the name is free-text."
                  value={draft.lock_cashout_name_to_owner}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, lock_cashout_name_to_owner: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Autofill cash-out email &amp; contact number
                  </label>
                  <select
                    className="w-full max-w-md rounded-md border bg-background px-3 py-2 text-sm"
                    value={draft.cashout_contact_autofill}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        cashout_contact_autofill: e.target
                          .value as TenantFeatures['cashout_contact_autofill'],
                      })
                    }
                  >
                    <option value="off">Off — member types both</option>
                    <option value="prefill">Autofill — pre-filled, member can edit</option>
                    <option value="locked">Locked — pre-filled, read-only</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Applies to payout-method fields tagged &quot;Email address&quot; / &quot;Contact
                    number&quot; in the Fees &amp; Tax → payout methods editor. Values come from the
                    member&apos;s account email and phone. In Locked mode the server enforces the
                    value; a member with no phone on file can still type one.
                  </p>
                </div>
                <Toggle
                  label="Require a payout QR code for cash-out"
                  description="When on, a member must have a saved QR code for the payout method they're using before they can request a cash-out — the QR is snapshotted onto the request so the disbursing clerk scans exactly what the member asked for. Members save theirs under Settings → Payout QR. When off (default), the QR is optional and payouts fall back to the typed account details."
                  value={draft.payout_qr_required}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, payout_qr_required: v })}
                />
                <Toggle
                  label="Unique cash-out account per member"
                  description="When on, a cash-out account number can't be reused by a different member — the first member to cash out with an account claims it (they may still reuse it across their own slots). Tag a payout-method field as 'Account number' in the Fees & Tax → payout methods editor to control which value is matched. When off, accounts aren't checked for uniqueness."
                  value={draft.unique_cashout_account_per_user}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, unique_cashout_account_per_user: v })}
                />
                <Toggle
                  label="Require verified KYC for Cash on Delivery"
                  description="When on, only KYC-verified members can pay with Cash on Delivery — COD is hidden at checkout for everyone else (and the order is rejected as a safety net). When off, COD is available to all members."
                  value={draft.cod_requires_kyc}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cod_requires_kyc: v })}
                />
                <Toggle
                  label={'Allow "Approve anyway" on unverified proof of payment'}
                  description="Normally an admin must type the reference number off the proof image before Approve unlocks. When on, an admin who can approve payments may approve WITHOUT verifying — they must give a written reason, and the override is recorded in the audit log. Use this when a reference number is genuinely unreadable. Infected uploads and duplicate reference numbers stay blocked either way."
                  value={draft.manual_payment_force_approve}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, manual_payment_force_approve: v })}
                />
                <div className="space-y-2 border-t pt-4">
                  <label className="text-sm font-medium">KYC document requirements</label>
                  <p className="text-xs text-muted-foreground">
                    The document slots a member uploads for review, in order. Each is an image
                    upload. Leave the list empty to fall back to the built-in starter set (gov ID
                    front/back + selfie) so KYC is never un-submittable.
                  </p>
                  <KycRequirementsEditor
                    value={draft.kyc_requirements ?? []}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, kyc_requirements: v })}
                  />
                </div>
              </CardContent>
            </Card>

            <Card id="messaging" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Messaging</CardTitle>
              </CardHeader>
              <CardContent>
                <Toggle
                  label="Enable buyer–seller chat"
                  description="When on, buyers can message the store (house products) and merchants from product pages; staff with the Messages permission answer house chats in the shared inbox. When off, the chat UI is hidden and chat endpoints are disabled."
                  value={draft.messaging_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, messaging_enabled: v })}
                />
                {draft.messaging_enabled && (
                  <div className="mt-4 space-y-3 rounded-md border bg-muted/20 p-3">
                    <Toggle
                      label="Auto-block spammers from chat"
                      description="Automatically blocks a member from messaging when they send too many messages in a short window. They keep full access to the rest of the platform — only chat is muted — and an admin can unblock them from the Messages inbox. Admin/merchant replies are never affected."
                      value={draft.chat_spam_autoblock_enabled}
                      disabled={!canWrite}
                      onChange={(v) => setDraft({ ...draft, chat_spam_autoblock_enabled: v })}
                    />
                    {draft.chat_spam_autoblock_enabled && (
                      <div className="flex flex-wrap items-end gap-3 text-sm">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Messages</label>
                          <Input
                            type="number"
                            min={1}
                            className="h-8 w-24"
                            disabled={!canWrite}
                            value={String(draft.chat_spam_threshold)}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                chat_spam_threshold: Math.max(
                                  1,
                                  Math.floor(Number(e.target.value) || 0),
                                ),
                              })
                            }
                          />
                        </div>
                        <span className="pb-1.5 text-xs text-muted-foreground">within</span>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Seconds</label>
                          <Input
                            type="number"
                            min={1}
                            className="h-8 w-24"
                            disabled={!canWrite}
                            value={String(draft.chat_spam_window_seconds)}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                chat_spam_window_seconds: Math.max(
                                  1,
                                  Math.floor(Number(e.target.value) || 0),
                                ),
                              })
                            }
                          />
                        </div>
                        <span className="pb-1.5 text-xs text-muted-foreground">
                          → auto chat-block
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card id="assistant" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>AI Assistant</CardTitle>
              </CardHeader>
              <CardContent>
                <Toggle
                  label="Enable the member AI Assistant"
                  description="A general-purpose AI assistant on the member portal (Assistant page): answers account questions from the member's own data, guides them around the platform, and helps with everyday questions in their language. Requires the server's Anthropic API key — without it, members see the scripted help fallback instead."
                  value={draft.assistant_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, assistant_enabled: v })}
                />
                {draft.assistant_enabled && (
                  <div className="mt-4 space-y-4 rounded-md border bg-muted/20 p-3">
                    <div className="flex flex-wrap items-end gap-3 text-sm">
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Model</label>
                        <Select
                          value={draft.assistant.model}
                          onValueChange={(v) =>
                            setDraft({
                              ...draft,
                              assistant: {
                                ...draft.assistant,
                                model: v as TenantFeatures['assistant']['model'],
                              },
                            })
                          }
                          disabled={!canWrite}
                        >
                          <SelectTrigger className="h-8 w-56">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="claude-haiku-4-5">
                              Haiku (default, most affordable)
                            </SelectItem>
                            <SelectItem value="claude-sonnet-5">
                              Sonnet (smarter, ~3× cost)
                            </SelectItem>
                            <SelectItem value="claude-opus-5">Opus (smartest, ~5× cost)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">
                          Messages / member / day
                        </label>
                        <Input
                          type="number"
                          min={0}
                          className="h-8 w-28"
                          disabled={!canWrite}
                          value={String(draft.assistant.daily_cap)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              assistant: {
                                ...draft.assistant,
                                daily_cap: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                              },
                            })
                          }
                        />
                        <p className="text-[11px] text-muted-foreground">0 = unlimited</p>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">
                          Monthly budget (USD)
                        </label>
                        <Input
                          className="h-8 w-28"
                          placeholder="e.g. 50"
                          disabled={!canWrite}
                          value={draft.assistant.monthly_budget_usd}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              assistant: {
                                ...draft.assistant,
                                monthly_budget_usd: e.target.value.replace(/[^0-9.]/g, ''),
                              },
                            })
                          }
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Store assistant only · Empty = unlimited
                        </p>
                      </div>
                    </div>
                    <Toggle
                      label="Charge members in AI Credits"
                      description="Meter the assistant per member: everyone gets a set number of free answers each month, and beyond that each answer spends one credit from their wallet. Members can buy credits (if you set a price), earn them from the comp plan, or get them with a product. Your monthly budget above still applies on top — if it runs out, everyone pauses, including members holding credits."
                      value={draft.assistant.credits_enabled}
                      disabled={!canWrite}
                      onChange={(v) =>
                        setDraft({
                          ...draft,
                          assistant: { ...draft.assistant, credits_enabled: v },
                        })
                      }
                    />
                    {draft.assistant.credits_enabled && (
                      <div className="flex flex-wrap items-end gap-3 rounded-md border bg-background p-3 text-sm">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">
                            Free answers / member / month
                          </label>
                          <Input
                            type="number"
                            min={0}
                            className="h-8 w-32"
                            disabled={!canWrite}
                            value={String(draft.assistant.credits_monthly_free)}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                assistant: {
                                  ...draft.assistant,
                                  credits_monthly_free: Math.max(0, Number(e.target.value) || 0),
                                },
                              })
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">You pay for these.</p>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Price per answer</label>
                          <Input
                            className="h-8 w-32"
                            placeholder="e.g. 5.00"
                            disabled={!canWrite}
                            value={draft.assistant.credits_price}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                assistant: {
                                  ...draft.assistant,
                                  credits_price: e.target.value.replace(/[^0-9.]/g, ''),
                                },
                              })
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Empty = not for sale (members can still earn them).
                          </p>
                        </div>
                        <CreditPricingHint usage={assistantUsageQuery.data} />
                      </div>
                    )}
                    <Toggle
                      label="Open the Assistant after login"
                      description="Members land on the Assistant instead of their dashboard — for tenants where the assistant is the main product. They can still reach the dashboard from the menu, and a link that already points somewhere specific (an unpaid order, a shared page) still wins."
                      value={draft.assistant.home_promotion}
                      disabled={!canWrite}
                      onChange={(v) =>
                        setDraft({
                          ...draft,
                          assistant: { ...draft.assistant, home_promotion: v },
                        })
                      }
                    />
                    <Toggle
                      label="Allow web search"
                      description="Lets the assistant search the internet for current outside-world facts (news, prices, definitions) — about $0.01 per search, counted against the monthly budget. Account questions never use web search."
                      value={draft.assistant.web_search_enabled}
                      disabled={!canWrite}
                      onChange={(v) =>
                        setDraft({
                          ...draft,
                          assistant: { ...draft.assistant, web_search_enabled: v },
                        })
                      }
                    />
                    <Toggle
                      label="Remember past conversations"
                      description="Lets the assistant carry context between a member's own chats, so they don't repeat themselves. Idle threads get a one-line summary (a fraction of a cent each, counted against the monthly budget) and the assistant can search their earlier chats. Members can switch this off for themselves."
                      value={draft.assistant.recall_enabled}
                      disabled={!canWrite}
                      onChange={(v) =>
                        setDraft({ ...draft, assistant: { ...draft.assistant, recall_enabled: v } })
                      }
                    />
                    {draft.assistant.recall_enabled && (
                      <div className="grid grid-cols-2 gap-3 pl-1">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">
                            Idle before summarising (minutes)
                          </label>
                          <Input
                            type="number"
                            min={5}
                            max={10080}
                            disabled={!canWrite}
                            value={draft.assistant.recall_idle_minutes}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                assistant: {
                                  ...draft.assistant,
                                  recall_idle_minutes: Number(e.target.value) || 0,
                                },
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">
                            Only summarise chats newer than (days)
                          </label>
                          <Input
                            type="number"
                            min={1}
                            max={365}
                            disabled={!canWrite}
                            value={draft.assistant.recall_digest_max_age_days}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                assistant: {
                                  ...draft.assistant,
                                  recall_digest_max_age_days: Number(e.target.value) || 0,
                                },
                              })
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Caps the backlog when first switching this on.
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        Company knowledge (fed to the assistant verbatim — story, product notes,
                        policies)
                      </label>
                      <Textarea
                        rows={6}
                        disabled={!canWrite}
                        value={draft.assistant.knowledge}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            assistant: { ...draft.assistant, knowledge: e.target.value },
                          })
                        }
                        placeholder="e.g. Our flagship product is… Orders ship within 3 days via LBC…"
                      />
                    </div>
                    <AssistantPersonaEditor
                      coachingEnabled={draft.coaching_enabled === true}
                      overrides={draft.assistant.personas}
                      disabled={!canWrite}
                      onChange={(personas) =>
                        setDraft({ ...draft, assistant: { ...draft.assistant, personas } })
                      }
                    />
                    {assistantUsageQuery.data && (
                      <AssistantUsagePanel usage={assistantUsageQuery.data} />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card id="autoship" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Auto-Ship</CardTitle>
              </CardHeader>
              <CardContent>
                <Toggle
                  label="Enable Auto-Ship"
                  description="When on, members can set up monthly auto-ship subscriptions that re-order from their wallet on a chosen day. When off, the member Auto-Ship page is hidden, new/edited subscriptions are rejected, and the scheduler skips all due runs — existing subscriptions are preserved and resume when re-enabled."
                  value={draft.autoship_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, autoship_enabled: v })}
                />
              </CardContent>
            </Card>

            <Card id="gift-checks" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Gift checks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Turn on gift checks"
                  description="When on, you set up gift check programs under Commerce → Gift Checks: a discount a member earns after buying a number of chosen products, or that you issue by hand. Members see theirs in the members area with a code and a QR, and the cashier applies one at checkout. When off, nothing counts toward a gift check and none can be used, and the pages and till buttons are hidden; gift checks already issued are kept and come back when you turn it on again."
                  value={draft.vouchers_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, vouchers_enabled: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="voucher-label">
                    What they are called
                  </label>
                  <Input
                    id="voucher-label"
                    value={draft.voucher_label ?? ''}
                    disabled={!canWrite}
                    placeholder="Gift Check"
                    maxLength={40}
                    onChange={(e) => setDraft({ ...draft, voucher_label: e.target.value })}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    The name members, cashiers and receipts see, singular (e.g. "Gift Check", "Treat
                    Card").
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="birthday-window">
                    Birthday claim stays open for
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="birthday-window"
                      type="number"
                      min={0}
                      max={31}
                      value={String(draft.birthday_claim_window_days ?? 0)}
                      disabled={!canWrite}
                      onChange={(e) => {
                        const n = Math.trunc(Number(e.target.value));
                        setDraft({
                          ...draft,
                          birthday_claim_window_days: Number.isFinite(n)
                            ? Math.min(31, Math.max(0, n))
                            : 0,
                        });
                      }}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">days after the birthday</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    For programs earned on the birthday. 0 means the member claims it on the day
                    itself; 6 gives them the birthday week. Counted in the tenant&apos;s timezone.
                  </p>
                </div>
                <Toggle
                  label="Birthday gift checks need a verified KYC"
                  description="Recommended: the birth date is only trustworthy once a reviewer checked it against an ID, and verifying KYC also locks it on the member's profile. Turn this off only if you do not run KYC."
                  value={draft.birthday_requires_kyc ?? true}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, birthday_requires_kyc: v })}
                />
              </CardContent>
            </Card>

            <Card id="member-resources" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Resources library</CardTitle>
              </CardHeader>
              <CardContent>
                <Toggle
                  label="Enable member Resources"
                  description="When on, members get a Resources page listing the documents (PDF / Excel / Word) and links you publish under Content → Resources — videos go in as links. When off, the member page and its API are hidden; nothing is deleted and uploaded files are untouched, so flipping it back restores the list as it was."
                  value={draft.member_resources_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, member_resources_enabled: v })}
                />
                <Toggle
                  label="View Office files in the browser"
                  description="When on, Excel / Word / PowerPoint resources open in a viewer inside the members area instead of only downloading. Those formats can't be rendered by a browser on their own, so the file is displayed by Microsoft's public Office viewer — which means a time-limited link to it is fetched by Microsoft's servers, and the document leaves your infrastructure while it is being viewed. Leave off if any resource is confidential; downloads still work either way, and PDFs and videos are unaffected."
                  value={draft.member_resources_office_viewer}
                  disabled={!canWrite || !draft.member_resources_enabled}
                  onChange={(v) => setDraft({ ...draft, member_resources_office_viewer: v })}
                />
              </CardContent>
            </Card>

            {(canWrite || draft.lms_enabled) && (
              <Card id="learning" className="scroll-mt-20">
                <CardHeader>
                  <CardTitle>Learning</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    The LMS master switch. It lives here rather than under Learning because the
                    whole Learning section of the sidebar is hidden while it is off.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Toggle
                    label="Enable the Learning Management System"
                    description="MASTER SWITCH (super admin only). When OFF (default) the LMS does not exist for this tenant: no course/creator API, no Learning section or Learning reports in admin, no My Courses / player / course pages for members, no creator portal, and the member dashboard stays MLM."
                    value={draft.lms_enabled ?? false}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, lms_enabled: v })}
                  />
                  {draft.lms_enabled && (
                    <p className="text-sm text-muted-foreground">
                      Everything else — the learner experience, content review, subscriptions,
                      creator fees, video limits and the course notification templates — is under{' '}
                      <Link to="/learning-settings" className="underline">
                        Learning → Learning Settings
                      </Link>
                      .
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {(canWrite || draft.coaching_enabled) && (
              <Card id="coaching" className="scroll-mt-20">
                <CardHeader>
                  <CardTitle>Coaching (Miracles Protocol)</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    The coach app: onboarding, goals and daily actions, the Success Score, the Vedic
                    chart and daily guidance. Only the tenant that sells the coach app turns this
                    on.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Toggle
                    label="Enable the coach app"
                    description="MASTER SWITCH (super admin only). When OFF (default) the coach app does not exist for this tenant: every coaching API route returns not found and the coach site says it is unavailable. Data members already saved is kept and returns when you turn it back on."
                    value={draft.coaching_enabled ?? false}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, coaching_enabled: v })}
                  />
                  {draft.coaching_enabled && (
                    <div className="space-y-4 rounded-md border bg-muted/20 p-3">
                      <div className="flex flex-wrap items-end gap-3 text-sm">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Astrology engine</label>
                          <Select
                            value={draft.coaching.astro_provider}
                            onValueChange={(v) =>
                              setDraft({
                                ...draft,
                                coaching: {
                                  ...draft.coaching,
                                  astro_provider: v as TenantFeatures['coaching']['astro_provider'],
                                },
                              })
                            }
                            disabled={!canWrite}
                          >
                            <SelectTrigger className="h-8 w-60">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="local">Built-in engine (free)</SelectItem>
                              <SelectItem value="prokerala">
                                Prokerala (needs the server API keys)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">AI model</label>
                          <Select
                            value={draft.coaching.model}
                            onValueChange={(v) =>
                              setDraft({
                                ...draft,
                                coaching: {
                                  ...draft.coaching,
                                  model: v as TenantFeatures['coaching']['model'],
                                },
                              })
                            }
                            disabled={!canWrite}
                          >
                            <SelectTrigger className="h-8 w-56">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="claude-haiku-4-5">
                                Haiku (default, most affordable)
                              </SelectItem>
                              <SelectItem value="claude-sonnet-5">
                                Sonnet (smarter, ~3× cost)
                              </SelectItem>
                              <SelectItem value="claude-opus-5">
                                Opus (smartest, ~5× cost)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {(
                          [
                            ['free_daily_messages', 'Free coach messages / day', '0 = unlimited'],
                            ['pro_daily_messages', 'Pro coach messages / day', '0 = unlimited'],
                            ['free_active_goals', 'Free active goals', '0 = unlimited'],
                            [
                              'astro_credit_alert_pct',
                              'Prokerala credit alert (%)',
                              'of the monthly pack',
                            ],
                            [
                              'astro_monthly_credits',
                              'Prokerala credits / month',
                              'your pack size; 0 = no pack',
                            ],
                          ] as const
                        ).map(([key, label, hint]) => (
                          <div key={key} className="space-y-1">
                            <label className="text-xs text-muted-foreground">{label}</label>
                            <Input
                              type="number"
                              min={key === 'astro_credit_alert_pct' ? 1 : 0}
                              max={key === 'astro_credit_alert_pct' ? 100 : undefined}
                              className="h-8 w-28"
                              disabled={!canWrite}
                              value={String(draft.coaching[key])}
                              onChange={(e) => {
                                const n = Math.max(0, Math.floor(Number(e.target.value) || 0));
                                setDraft({
                                  ...draft,
                                  coaching: {
                                    ...draft.coaching,
                                    [key]:
                                      key === 'astro_credit_alert_pct'
                                        ? Math.min(100, Math.max(1, n))
                                        : n,
                                  },
                                });
                              }}
                            />
                            <p className="text-[11px] text-muted-foreground">{hint}</p>
                          </div>
                        ))}
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">
                            Monthly AI budget (USD)
                          </label>
                          <Input
                            className="h-8 w-28"
                            placeholder="e.g. 50"
                            disabled={!canWrite}
                            value={draft.coaching.monthly_budget_usd}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                coaching: {
                                  ...draft.coaching,
                                  monthly_budget_usd: e.target.value.replace(/[^0-9.]/g, ''),
                                },
                              })
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Master Kala and coaching text · Empty = unlimited
                          </p>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Community link</label>
                          <Input
                            className="h-8 w-72"
                            placeholder="https://t.me/… or a Facebook group"
                            disabled={!canWrite}
                            value={draft.coaching.community_url ?? ''}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                coaching: {
                                  ...draft.coaching,
                                  community_url: e.target.value.trim(),
                                },
                              })
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">
                            A card on the coach app’s Home and Profile · https only · Empty = no
                            card
                          </p>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Community label</label>
                          <Input
                            className="h-8 w-48"
                            placeholder="Join the community"
                            maxLength={40}
                            disabled={!canWrite}
                            value={draft.coaching.community_label ?? ''}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                coaching: { ...draft.coaching, community_label: e.target.value },
                              })
                            }
                          />
                          <p className="text-[11px] text-muted-foreground">The card’s title</p>
                        </div>
                      </div>
                      <Toggle
                        label="Keep Prokerala charts"
                        description="On (default): each member's Prokerala chart is kept until their birth details change, so it costs credits only once. Off: the chart is fetched again after 24 hours, which uses credits every day the member returns."
                        value={draft.coaching.astro_natal_cache_hours === null}
                        disabled={!canWrite}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            coaching: { ...draft.coaching, astro_natal_cache_hours: v ? null : 24 },
                          })
                        }
                      />
                      <Toggle
                        label="Subscription required to use the app"
                        description="No free tier: a member without a live Pro or Elite window sees only the paywall (and, in the Android app, a link to subscribe on the website). Their store, orders and courses stay open. Off (default): the free tier works as before."
                        value={draft.coaching.paid_only === true}
                        disabled={!canWrite}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            coaching: { ...draft.coaching, paid_only: v },
                          })
                        }
                      />
                      <div className="flex flex-wrap gap-4">
                        {(['pro', 'elite'] as const).map((tier) => (
                          <div key={tier} className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              {tier === 'pro'
                                ? 'First paid tier is called'
                                : 'Second paid tier is called'}
                            </label>
                            <Input
                              className="h-8 w-48"
                              maxLength={30}
                              disabled={!canWrite}
                              value={draft.coaching.tier_labels?.[tier] ?? ''}
                              placeholder={tier === 'pro' ? 'Success Pro' : 'Success Elite'}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  coaching: {
                                    ...draft.coaching,
                                    tier_labels: {
                                      pro: draft.coaching.tier_labels?.pro ?? 'Success Pro',
                                      elite: draft.coaching.tier_labels?.elite ?? 'Success Elite',
                                      [tier]: e.target.value,
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                        ))}
                        <p className="basis-full text-[11px] text-muted-foreground">
                          The names members see on the paywall, the upgrade page and every lock (for
                          example Standard and Premium). Plans, grants and the feature table keep
                          working under the hood whatever they are called.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Frequency categories</p>
                        <p className="text-[11px] text-muted-foreground">
                          The listening intentions a frequency track is filed under, in this order
                          (for example Mantra, Affirmation, Sleep). Leave the list empty to keep the
                          seven built-in categories. The key is the stable name tracks are filed
                          under; it fills itself in from the label. Hue (0 to 360) colours the tile
                          and the player; leave it blank for an automatic one.
                        </p>
                        <RepeatableRows
                          items={(draft.coaching.frequency_categories ?? []).map((c) => ({
                            key: c.key,
                            label: c.label,
                            hue: c.hue === null || c.hue === undefined ? '' : String(c.hue),
                          }))}
                          onChange={(rows) =>
                            setDraft({
                              ...draft,
                              coaching: {
                                ...draft.coaching,
                                frequency_categories: rows.map((r) => {
                                  const label = String(r.label ?? '');
                                  const typedKey = String(r.key ?? '');
                                  const hueNum = Number.parseInt(String(r.hue ?? ''), 10);
                                  return {
                                    key: slugKey(typedKey || label),
                                    label,
                                    hue: Number.isFinite(hueNum)
                                      ? Math.min(360, Math.max(0, hueNum))
                                      : null,
                                  };
                                }),
                              },
                            })
                          }
                          addLabel="Add category"
                          columns={3}
                          makeBlank={() => ({ key: '', label: '', hue: '' })}
                          fields={[
                            { kind: 'text', key: 'label', label: 'Label', placeholder: 'Mantra' },
                            {
                              kind: 'text',
                              key: 'key',
                              label: 'Key',
                              placeholder: 'mantra',
                              mono: true,
                            },
                            {
                              kind: 'text',
                              key: 'hue',
                              label: 'Hue (0-360)',
                              placeholder: '30',
                              mono: true,
                            },
                          ]}
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Library categories</p>
                        <p className="text-[11px] text-muted-foreground">
                          How the content library (Coaching &rarr; Library) is grouped, in this
                          order. Leave it empty for one plain list.
                        </p>
                        <RepeatableRows
                          items={(draft.coaching.library_categories ?? []).map((c) => ({
                            key: c.key,
                            label: c.label,
                          }))}
                          onChange={(rows) =>
                            setDraft({
                              ...draft,
                              coaching: {
                                ...draft.coaching,
                                library_categories: rows.map((r) => {
                                  const label = String(r.label ?? '');
                                  const typedKey = String(r.key ?? '');
                                  return { key: slugKey(typedKey || label), label };
                                }),
                              },
                            })
                          }
                          addLabel="Add library category"
                          columns={2}
                          makeBlank={() => ({ key: '', label: '' })}
                          fields={[
                            { kind: 'text', key: 'label', label: 'Label', placeholder: 'Mindset' },
                            {
                              kind: 'text',
                              key: 'key',
                              label: 'Key',
                              placeholder: 'mindset',
                              mono: true,
                            },
                          ]}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">
                          Pesos per US dollar (for the Credits page's cost figures)
                        </label>
                        <Input
                          className="h-8 w-28"
                          inputMode="decimal"
                          disabled={!canWrite}
                          value={String(draft.coaching.usd_php_rate ?? 57)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              coaching: {
                                ...draft.coaching,
                                usd_php_rate: Number(e.target.value.replace(/[^0-9.]/g, '')) || 57,
                              },
                            })
                          }
                        />
                        <p className="text-[11px] text-muted-foreground">
                          What each action costs in credits, the packs on sale and the month's
                          figures are on Coaching → Credits.
                        </p>
                      </div>
                      <Toggle
                        label="Write guidance and plans with AI"
                        description="Uses the AI model to phrase each member's first plan and daily guidance around their own goal, inside the coaching AI budget above. Off = the built-in wording only, at no cost."
                        value={draft.coaching.daily_engine_enabled}
                        disabled={!canWrite}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            coaching: { ...draft.coaching, daily_engine_enabled: v },
                          })
                        }
                      />
                      <Toggle
                        label="Streak freeze for Pro members"
                        description="Pro members can keep their streak alive across one missed day each month."
                        value={draft.coaching.streak_freeze_pro}
                        disabled={!canWrite}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            coaching: { ...draft.coaching, streak_freeze_pro: v },
                          })
                        }
                      />
                      <Toggle
                        label="Chart and daily guidance need Pro"
                        description="Off (default): every member sees their birth chart and today's guidance. On: they are part of the Pro plan, and free members see an upgrade prompt instead."
                        value={draft.coaching.chart_guidance_pro_only}
                        disabled={!canWrite}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            coaching: { ...draft.coaching, chart_guidance_pro_only: v },
                          })
                        }
                      />
                      <Toggle
                        label="Align tools and the written weekly summary need Pro"
                        description="Off (default): every member can use breathwork, frequencies and biorhythm, and gets a written summary of their weekly review. On: free members see an upgrade prompt on the three tools and close their week with the built-in summary. The 369 practice and the vision board stay free either way."
                        value={draft.coaching.tools_pro_only}
                        disabled={!canWrite}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            coaching: { ...draft.coaching, tools_pro_only: v },
                          })
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Plans, content, members and usage live on the{' '}
                        <Link to="/coaching" className="underline underline-offset-2">
                          Coaching page
                        </Link>
                        .
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card id="member-subscription" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Member subscription</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Enable member subscription"
                  description="When on, members can subscribe monthly (paid from their slot wallet) to unlock the Special Links (Zoom/Vimeo live) you publish under Content → Special Links. When off, the Subscribe page + special-links surface are hidden and the renewal engine is a no-op — existing paid periods still run out."
                  value={draft.member_subscription_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, member_subscription_enabled: v })}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Monthly price</label>
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      value={draft.member_subscription.price}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          member_subscription: {
                            ...draft.member_subscription,
                            price: e.target.value,
                          },
                        })
                      }
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Charged from the member's chosen slot wallet each month.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Wallet charged</label>
                    <select
                      className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                      value={draft.member_subscription.currency}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          member_subscription: {
                            ...draft.member_subscription,
                            currency: e.target.value as 'cash' | 'gc',
                          },
                        })
                      }
                    >
                      <option value="cash">Cash wallet</option>
                      <option value="gc">GC wallet</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="sponsor" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Registration &amp; default sponsor</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Enable sign up</label>
                  <p className="text-xs text-muted-foreground">
                    Controls who may register. <b>On</b> (default) allows open registration via the
                    signup page and links. <b>Links only</b> permits registration only through a
                    referral or product-referral link (a{' '}
                    <code className="rounded bg-muted px-1">?ref=</code> code).
                    <b> By sponsor only</b> requires the standalone signup page to include a sponsor
                    code.
                  </p>
                  <Select
                    value={draft.signup_mode}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({ ...draft, signup_mode: v as TenantFeatures['signup_mode'] })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on">On (default)</SelectItem>
                      <SelectItem value="links_only">Links only</SelectItem>
                      <SelectItem value="by_sponsor_only">By sponsor only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Toggle
                  label="Campaign pages and courses count as an invite"
                  description="Only applies when Enable sign up is 'Links only' or 'By sponsor only'. When on, a visitor with no referral code may still register if they arrive through an ENABLED campaign page (the ref-less link a Virtual product opens) or are joining to buy a course from its page. They are placed under the Walk-in default sponsor below. The campaign page and the course are checked by the server, so a mistyped, disabled or unpublished one still cannot get anyone in. Off by default: invitation-only stays invitation-only unless you widen it."
                  value={draft.campaign_page_signup_allowed}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, campaign_page_signup_allowed: v })}
                />
                <div className="space-y-2">
                  <label className="text-sm font-medium">Walk-in default sponsor slot</label>
                  <p className="text-xs text-muted-foreground">
                    The slot that sponsors self-signups arriving without a referral link (
                    <code className="rounded bg-muted px-1">/signup</code> with no{' '}
                    <code className="rounded bg-muted px-1">?ref=</code>). New walk-ins register
                    directly under this slot. Leave empty to fall back to the legacy default member
                    below, then super_admin.
                  </p>
                  <SlotCodePicker
                    value={draft.default_sponsor_slot_code ?? null}
                    onChange={(code) => setDraft({ ...draft, default_sponsor_slot_code: code })}
                    disabled={!canWrite}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Link registration override slot</label>
                  <p className="text-xs text-muted-foreground">
                    When set, <b>every</b> link signup (
                    <code className="rounded bg-muted px-1">?ref=</code>) registers under this slot
                    regardless of which link was used — useful to funnel a campaign. Leave empty for
                    normal per-link behavior (register under the link&apos;s slot).
                  </p>
                  <SlotCodePicker
                    value={draft.link_sponsor_override_slot_code ?? null}
                    onChange={(code) =>
                      setDraft({ ...draft, link_sponsor_override_slot_code: code })
                    }
                    disabled={!canWrite}
                  />
                </div>

                <Toggle
                  label="Self-stack extra slots"
                  description="When on, a member's 2nd-and-later slots suggest the member's own first slot as the sponsor (editable at activation). When off, extra slots suggest the member's original signup sponsor. A member's first slot always uses their registration sponsor."
                  value={draft.extra_slots_self_sponsor}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, extra_slots_self_sponsor: v })}
                />

                <Toggle
                  label="Extra slots keep the member's registration slot"
                  description="Only applies while 'Self-stack extra slots' is OFF. When on (default), a member's 2nd-and-later slots default to the exact slot they registered under — their replicated link's slot, or the walk-in default slot — instead of their sponsor's OLDEST slot. This matters when a sponsor owns many slots: with it off, a recruit who signed up through the sponsor's 29th link has every extra slot land under the sponsor's 1st slot. It sets the DEFAULT only — a member who types a sponsor (where 'Sponsor editable on extra slots' allows it) still overrides it, and it has no effect on the genealogy 'place a slot here' dialog, which pre-fills the seat's own sponsor."
                  value={draft.extra_slots_use_registration_slot}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, extra_slots_use_registration_slot: v })}
                />

                <Toggle
                  label="Sponsor editable from replicated link"
                  description="Controls the FIRST slot of a member who registered via a replicated/referral link. When on (default), they may override the sponsor at activation. When off, the sponsor is LOCKED to the link's sponsor. (Walk-in signups are always editable; an admin sponsor override always wins.)"
                  value={draft.sponsor_editable_from_replicated_link}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, sponsor_editable_from_replicated_link: v })}
                />

                <Toggle
                  label="Sponsor editable on extra slots"
                  description="Controls a member's 2nd-and-later slots while 'Self-stack extra slots' is OFF. When on (default), they may override the pre-filled sponsor at activation. When off, it's LOCKED to whatever the toggle above resolves — the member's registration slot, or the signup sponsor's oldest slot."
                  value={draft.sponsor_editable_extra_slots}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, sponsor_editable_extra_slots: v })}
                />

                <Toggle
                  label="Sponsor editable when self-stacking"
                  description="Controls a member's 2nd-and-later slots while 'Self-stack extra slots' is ON. When on (default), they may override their own slot as sponsor at activation. When off, it's LOCKED to their own first slot."
                  value={draft.sponsor_editable_self_stack}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, sponsor_editable_self_stack: v })}
                />

                <Toggle
                  label="Activate downline: direct only"
                  description="Controls who a leader may spend a vault code on via 'Use a code for an affiliate'. When on (default), only their DIRECT downline — because that path forces the LEADER as the new slot's sponsor, so at any depth it can pull a recruit off the leg whose replicated link brought them in, activation commission included. When off, anyone in their sponsor chain. Turning it on removes nothing: transferring the code still reaches any depth, and it's safer — a transfer creates no sponsor link, so the recipient activates under their own registered sponsor."
                  value={draft.redeem_for_downline_direct_only}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, redeem_for_downline_direct_only: v })}
                />

                <Toggle
                  label="Show sponsor on dashboard"
                  description="When on, the member dashboard's 'Your standing' card shows the member's sponsor (the owner of their active slot). Display-only. Default off."
                  value={draft.show_sponsor_on_dashboard}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, show_sponsor_on_dashboard: v })}
                />

                <Toggle
                  label="Show Total Saved counter on member dashboard"
                  description="When on, the member dashboard shows a 'Total saved' card: the lifetime sum of all discounts the member received across online-store and cashier purchases (membership/rank/stockist prices, coupons, and manager discounts). Display-only. Default off."
                  value={draft.total_saved_counter_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, total_saved_counter_enabled: v })}
                />

                <Toggle
                  label="Show sponsor field during slot activation"
                  description="When on (default), the redeem/activation form shows the sponsor field. When off, it's HIDDEN for members who have an admin sponsor override (set per member on their detail page) — so an overridden member can't see they were overridden. Members without an override always see the field."
                  value={draft.show_sponsor_on_redeem}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, show_sponsor_on_redeem: v })}
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Legacy default sponsor (member)
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Superseded by the walk-in slot above. Used only when no walk-in slot is set; the
                    system then sponsors under this member&apos;s first active slot.
                  </p>
                  <DefaultSponsorPicker
                    value={draft.default_sponsor_user_id ?? null}
                    onChange={(id) => setDraft({ ...draft, default_sponsor_user_id: id })}
                    disabled={!canWrite}
                  />
                </div>
              </CardContent>
            </Card>

            <Card id="registration-fields" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Custom registration fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Extra fields collected on the member signup form, beyond the built-in
                  name/email/phone. Each is a text input or a file upload. Captured values appear on
                  the member's detail page.
                </p>
                <RegistrationFieldsEditor
                  value={draft.registration_fields}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, registration_fields: v })}
                />
                <div className="space-y-1 border-t pt-3">
                  <label className="text-sm font-medium" htmlFor="birth-date-field">
                    Birth date
                  </label>
                  <Select
                    value={draft.birth_date_field ?? 'hidden'}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        birth_date_field: v as TenantFeatures['birth_date_field'],
                      })
                    }
                  >
                    <SelectTrigger id="birth-date-field" className="w-full max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hidden">Don&apos;t ask</SelectItem>
                      <SelectItem value="optional">Ask, optional</SelectItem>
                      <SelectItem value="required">Ask, required</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Asked on the signup forms and shown on the member&apos;s profile, where they can
                    change it until they submit their KYC. After that only an administrator can.
                    Birthday gift checks need it. Members who signed up before you turn it on can
                    add it from their profile. If members sign up in a mobile app, choose Required
                    only once they have the updated app: an older app cannot send the date, so its
                    signups would be refused.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card id="profile-editing" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Profile editing</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold">Member profile</div>
                    <p className="text-xs text-muted-foreground">
                      Which fields members may change on their Profile tab. Email, username, and
                      address are always shown read-only there (address is managed from the member's
                      own Address tab).
                    </p>
                  </div>
                  <Toggle
                    label="Name"
                    description="First, middle, and last name."
                    value={draft.member_profile_editable.name}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        member_profile_editable: { ...draft.member_profile_editable, name: v },
                      })
                    }
                  />
                  <Toggle
                    label="Phone"
                    value={draft.member_profile_editable.phone}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        member_profile_editable: { ...draft.member_profile_editable, phone: v },
                      })
                    }
                  />
                  <Toggle
                    label="Profile photo"
                    description="Upload or remove their avatar."
                    value={draft.member_profile_editable.profilePicture}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        member_profile_editable: {
                          ...draft.member_profile_editable,
                          profilePicture: v,
                        },
                      })
                    }
                  />
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold">Merchant profile</div>
                    <p className="text-xs text-muted-foreground">
                      Which fields merchants may change on their Profile page. The account login
                      email &amp; username stay read-only.
                    </p>
                  </div>
                  <Toggle
                    label="Business name"
                    value={draft.merchant_profile_editable.businessName}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        merchant_profile_editable: {
                          ...draft.merchant_profile_editable,
                          businessName: v,
                        },
                      })
                    }
                  />
                  <Toggle
                    label="Owner name"
                    description="The merchant account's display name."
                    value={draft.merchant_profile_editable.ownerName}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        merchant_profile_editable: {
                          ...draft.merchant_profile_editable,
                          ownerName: v,
                        },
                      })
                    }
                  />
                  <Toggle
                    label="Contact number"
                    value={draft.merchant_profile_editable.contactNumber}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        merchant_profile_editable: {
                          ...draft.merchant_profile_editable,
                          contactNumber: v,
                        },
                      })
                    }
                  />
                  <Toggle
                    label="Contact email"
                    value={draft.merchant_profile_editable.contactEmail}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        merchant_profile_editable: {
                          ...draft.merchant_profile_editable,
                          contactEmail: v,
                        },
                      })
                    }
                  />
                  <Toggle
                    label="Pickup address &amp; region"
                    description="The shipping origin used to compute courier fees."
                    value={draft.merchant_profile_editable.pickupAddress}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        merchant_profile_editable: {
                          ...draft.merchant_profile_editable,
                          pickupAddress: v,
                        },
                      })
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <Card id="referral-codes" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Member referral codes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Toggle
                  label="Let members set their own referral code"
                  description="When on, members can change their referral code from Settings. A code that's already been used stays reserved to them even after they change it; release it from the member's Access tab."
                  value={draft.member_referral_code_self_set}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, member_referral_code_self_set: v })}
                />
                <div className="flex items-center gap-3">
                  <label className="text-sm">Max changes per member</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={draft.referral_code_max_changes}
                    disabled={!canWrite || !draft.member_referral_code_self_set}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        referral_code_max_changes: Math.max(0, Number(e.target.value) || 0),
                      })
                    }
                    className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
                  />
                  <span className="text-xs text-muted-foreground">0 disables changes.</span>
                </div>
              </CardContent>
            </Card>

            <Card id="dropshipping-income" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Dropshipping Income</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Toggle
                  label="Enable Dropshipping Income (Share & Earn button)"
                  description="Shows/hides the “Share & Earn” button on each storefront product page. When on, members can share a per-product replicated link (/product/{id}?ref=…) via that button; an outside buyer who registers and buys through it pays full SRP, becomes a lead of the sharer, and the sharer earns the retail margin (SRP − their own discounted price) on each discounted product. When off, the button is hidden."
                  value={draft.dropshipping_income}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, dropshipping_income: v })}
                />
                <Toggle
                  label="Store-wide Share & Earn link"
                  description="When on, members get a link to the WHOLE store (/s/…) — a lead who lands through it shops the storefront at retail prices and everything they buy in that visit is credited to the sharer as one Dropshipping Income margin, exactly like a per-product Share & Earn sale. Attribution ends when the order is placed. Needs Dropshipping Income on."
                  value={draft.store_wide_dropship_link}
                  disabled={!canWrite || !draft.dropshipping_income}
                  onChange={(v) => setDraft({ ...draft, store_wide_dropship_link: v })}
                />
                <Toggle
                  label="Limit activation kits to the campaign's packages"
                  description="When on, a lead who signs up through a campaign link can only buy the activation kits tagged to that campaign (Replicated Pages → Packages), plus every regular product; other kits are hidden until they activate. A campaign that tags nothing is unrestricted. When off, every activation kit is available to every lead."
                  value={draft.package_replicated_link_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, package_replicated_link_enabled: v })}
                />
                <Toggle
                  label="Pay the inviter on campaign-page sales"
                  description="When on, a visitor who buys through someone's campaign-page product button (they register, then pay) is treated as that member's dropship sale, so the inviter earns the retail margin — otherwise the sale pays nobody, because the buyer holds no slot yet. Note this inherits the Dropshipping Income terms above: the buyer is charged SRP (that margin IS the inviter's income) and the order is paid by uploading proof, not by wallet or online gateway. Needs Dropshipping Income on."
                  value={draft.virtual_funnel_dropship_attribution}
                  disabled={!canWrite || !draft.dropshipping_income}
                  onChange={(v) => setDraft({ ...draft, virtual_funnel_dropship_attribution: v })}
                />
                <Toggle
                  label="Credit replicated-link PV to the link owner"
                  description="When on, a non-activated buyer who purchases at SRP through a replicated link credits the link owner's PV as if the owner bought it (owner's personal PV plus their upline's group PV). The buyer's own PV is untouched. Independent of the cash margin above — either can be on alone."
                  value={draft.replicated_link_pv_to_owner}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, replicated_link_pv_to_owner: v })}
                />
                <Toggle
                  label="Encrypt the referral code in share links"
                  description="When on, shared links carry an opaque token (?ref=e1.…) instead of the member's plaintext referral code. Existing plaintext links keep working — flipping this never breaks links already shared."
                  value={draft.replicated_link_ref_encrypted}
                  disabled={!canWrite || !draft.dropshipping_income}
                  onChange={(v) => setDraft({ ...draft, replicated_link_ref_encrypted: v })}
                />
              </CardContent>
            </Card>

            <Card id="terms" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Terms &amp; Privacy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Require Terms acceptance at signup"
                  description="When on, new members must accept the current Terms before signing up, and existing members are re-prompted whenever you publish a new version. Leave off until you've authored the text below."
                  value={draft.require_terms_on_signup}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, require_terms_on_signup: v })}
                />
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium" htmlFor="terms-text">
                      Terms of Service
                    </label>
                    <span className="text-xs text-muted-foreground">v{draft.terms_version}</span>
                  </div>
                  <Textarea
                    id="terms-text"
                    rows={6}
                    value={draft.terms_text}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, terms_text: e.target.value })}
                    placeholder="Paste your Terms of Service here…"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium" htmlFor="privacy-text">
                      Privacy Policy
                    </label>
                    <span className="text-xs text-muted-foreground">
                      v{draft.privacy_policy_version}
                    </span>
                  </div>
                  <Textarea
                    id="privacy-text"
                    rows={6}
                    value={draft.privacy_policy_text}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, privacy_policy_text: e.target.value })}
                    placeholder="Paste your Privacy Policy here…"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Editing the text and saving publishes a new version automatically. Members who
                  accepted an older version are asked to re-accept on their next visit.
                </p>
              </CardContent>
            </Card>

            <Card id="activation" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Activation &amp; Registration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Require username at registration"
                  description="When on, new members must choose a unique username at signup."
                  value={draft.require_username_on_signup}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, require_username_on_signup: v })}
                />
                <Toggle
                  label="Allow login by username"
                  description="When on, members may sign in with their username or email. Admin and cashier logins stay email-only."
                  value={draft.allow_login_by_username}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, allow_login_by_username: v })}
                />
                <Toggle
                  label="Require activation terms"
                  description="When on, members must accept the activation agreement below before redeeming a code to activate a slot."
                  value={draft.require_activation_terms}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, require_activation_terms: v })}
                />
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium" htmlFor="activation-terms-text">
                      Activation agreement
                    </label>
                    <span className="text-xs text-muted-foreground">
                      v{draft.activation_terms_version}
                    </span>
                  </div>
                  <Textarea
                    id="activation-terms-text"
                    rows={5}
                    value={draft.activation_terms_text}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, activation_terms_text: e.target.value })}
                    placeholder="e.g. Activation cannot be changed once done…"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Editing the text and saving publishes a new version automatically.
                </p>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="activation-success-message">
                    Activation success message
                  </label>
                  <Textarea
                    id="activation-success-message"
                    rows={2}
                    value={draft.activation_success_message}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({ ...draft, activation_success_message: e.target.value })
                    }
                    placeholder="e.g. Congratulations! Your slot is now active."
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown to the member right after they activate a slot by redeeming a code.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="order-success-message">
                    Order success message
                  </label>
                  <Textarea
                    id="order-success-message"
                    rows={2}
                    value={draft.order_success_message}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, order_success_message: e.target.value })}
                    placeholder="e.g. Thank you for your order! We've received it and will process it shortly."
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown in the storefront "Order placed!" dialog after a successful checkout.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="unpaid-orders-dialog-message">
                    Unpaid orders dialog message
                  </label>
                  <Textarea
                    id="unpaid-orders-dialog-message"
                    rows={2}
                    value={draft.unpaid_orders_dialog_message}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({ ...draft, unpaid_orders_dialog_message: e.target.value })
                    }
                    placeholder="e.g. You have unpaid order/s, would you like to proceed with the payment steps?"
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown to a member on login when they have one or more unpaid orders.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card id="signup-free-slot" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Free slot at signup</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Give every new member a free slot"
                  description="For a loyalty program where members never buy a kit: each member who signs up gets an active free slot straight away, so they have a wallet to top up and pay from. The slot is placed under the member's sponsor the same way a cashier's Activate free slot does, and pays no entry commission. Members who signed up before you turn this on keep what they have; a cashier can still activate a free slot for them."
                  value={draft.signup_free_slot?.enabled ?? false}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      signup_free_slot: {
                        enabled: v,
                        membership_id: draft.signup_free_slot?.membership_id ?? null,
                      },
                    })
                  }
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="signup-free-slot-membership">
                    Membership of the free slot
                  </label>
                  <Select
                    value={draft.signup_free_slot?.membership_id ?? ''}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        signup_free_slot: {
                          enabled: draft.signup_free_slot?.enabled ?? false,
                          membership_id: v || null,
                        },
                      })
                    }
                  >
                    <SelectTrigger id="signup-free-slot-membership" className="w-full max-w-md">
                      <SelectValue placeholder="Pick a membership" />
                    </SelectTrigger>
                    <SelectContent>
                      {fsEligibleMemberships.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only memberships marked free-slot eligible on the Memberships page are listed.
                    {fsEligibleMemberships.length === 0 && ' None is yet, so mark one there first.'}
                  </p>
                </div>
                <FreeSlotBackfill
                  enabled={Boolean(
                    featuresQuery.data?.signup_free_slot?.enabled &&
                    featuresQuery.data.signup_free_slot.membership_id,
                  )}
                  canWrite={canWrite}
                />
              </CardContent>
            </Card>

            <Card id="merchants" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Merchants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="platform-fee">
                    Default platform fee (%)
                  </label>
                  <Input
                    id="platform-fee"
                    type="number"
                    min={0}
                    max={100}
                    value={draft.platform_fee_pct ?? ''}
                    disabled={!canWrite}
                    onChange={(e) => setDraft({ ...draft, platform_fee_pct: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    The default a merchant's product fee starts at. Charged on every merchant sale
                    and snapshotted onto each product at creation (admin can override per product at
                    approval). The merchant earns price × (1 − fee%); the platform keeps the fee.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="platform-fee-min">
                      Min platform fee (%)
                    </label>
                    <Input
                      id="platform-fee-min"
                      type="number"
                      min={0}
                      max={100}
                      value={draft.platform_fee_min_pct ?? ''}
                      disabled={!canWrite}
                      onChange={(e) => setDraft({ ...draft, platform_fee_min_pct: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="platform-fee-max">
                      Max platform fee (%)
                    </label>
                    <Input
                      id="platform-fee-max"
                      type="number"
                      min={0}
                      max={100}
                      value={draft.platform_fee_max_pct ?? ''}
                      disabled={!canWrite}
                      onChange={(e) => setDraft({ ...draft, platform_fee_max_pct: e.target.value })}
                    />
                  </div>
                  <p className="col-span-2 text-xs text-muted-foreground">
                    The band a merchant may declare their own fee within. The server clamps any
                    merchant-entered fee into this range on submit and at approval.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="merchant-pv-pct">
                    Default PV (%)
                  </label>
                  <Input
                    id="merchant-pv-pct"
                    type="number"
                    min={0}
                    max={100}
                    value={draft.merchant_pv_commission_pct ?? ''}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({ ...draft, merchant_pv_commission_pct: e.target.value })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Pre-filled at product review. PV for a merchant product = PV% × commission
                    (commission = price × fee%). Admin can override per product at approval.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Realtime Unilevel defaults</label>
                  <div className="max-w-[10rem] space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="rtu-ceiling">
                      Pool ceiling (%)
                    </label>
                    <Input
                      id="rtu-ceiling"
                      type="number"
                      min={0}
                      max={100}
                      value={draft.merchant_rtu_ceiling_pct ?? ''}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({ ...draft, merchant_rtu_ceiling_pct: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">
                      Per-level shares (% of pool)
                    </span>
                    <table className="text-sm">
                      <thead className="text-left text-muted-foreground">
                        <tr>
                          <th className="py-1 pr-3 font-normal">Level</th>
                          <th className="py-1 pr-3 font-normal">Share %</th>
                          <th className="py-1" />
                        </tr>
                      </thead>
                      <tbody>
                        {(draft.merchant_rtu_default_shares ?? []).map((sh, idx) => (
                          <tr key={idx}>
                            <td className="py-1 pr-3 font-mono text-xs">
                              {idx === 0 ? 'L0 — buyer' : `L${idx}`}
                            </td>
                            <td className="py-1 pr-3">
                              <Input
                                className="h-8 w-24"
                                type="number"
                                min={0}
                                max={100}
                                value={sh}
                                disabled={!canWrite}
                                onChange={(e) =>
                                  setDraft({
                                    ...draft,
                                    merchant_rtu_default_shares: (
                                      draft.merchant_rtu_default_shares ?? []
                                    ).map((v, i) => (i === idx ? e.target.value : v)),
                                  })
                                }
                              />
                            </td>
                            <td className="py-1">
                              {canWrite && (draft.merchant_rtu_default_shares ?? []).length > 1 && (
                                <button
                                  type="button"
                                  className="text-xs text-muted-foreground hover:text-destructive"
                                  onClick={() =>
                                    setDraft({
                                      ...draft,
                                      merchant_rtu_default_shares: (
                                        draft.merchant_rtu_default_shares ?? []
                                      ).filter((_, i) => i !== idx),
                                    })
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {canWrite && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setDraft({
                            ...draft,
                            merchant_rtu_default_shares: [
                              ...(draft.merchant_rtu_default_shares ?? []),
                              '0',
                            ],
                          })
                        }
                      >
                        Add level
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Pre-fill the approval dialog's Realtime Unilevel tab. The pool = ceiling% ×
                    commission; each row is that level's % of the pool (L0 = buyer cashback).
                    Editable per product at approval.
                  </p>
                </div>
                <Toggle
                  label="Merchant sales earn MLM commissions"
                  description="When OFF (default) merchant sales are marketplace-only: no PV, no comp — they only credit the merchant wallet. When ON, a completed merchant order also fires the comp event like a house sale."
                  value={draft.merchant_sales_earn_comp}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, merchant_sales_earn_comp: v })}
                />
                <Toggle
                  label="Order by Area — auto-route fulfillment"
                  description="When ON, an ecom ship order's fulfilling branch is auto-selected from the buyer's shipping zone — the branch/stockist tagged for that area (else the Main branch) — instead of the member picking one. Tag each branch's service areas + set the Main branch on the branch detail page. Default OFF."
                  value={draft.order_by_area}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, order_by_area: v })}
                />
                <Toggle
                  label="Stockist receivables"
                  description="When ON, a stockist that fulfills a house ecom order accrues a receivable from Main (the full retail sale value), reversed on refund. View balances under Reports → Stockist Receivables. Default OFF."
                  value={draft.stockist_receivables}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, stockist_receivables: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Payout schedule</label>
                  <div className="flex gap-1">
                    {(['weekly', 'biweekly', 'monthly'] as const).map((s) => (
                      <Button
                        key={s}
                        type="button"
                        size="sm"
                        variant={draft.merchant_payout_schedule === s ? 'default' : 'outline'}
                        className="capitalize"
                        disabled={!canWrite}
                        onClick={() => setDraft({ ...draft, merchant_payout_schedule: s })}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Cadence you batch merchant cash-outs on (informational).
                  </p>
                </div>
                <MerchantRequirementsEditor
                  value={draft.merchant_requirements ?? []}
                  disabled={!canWrite}
                  onChange={(reqs) => setDraft({ ...draft, merchant_requirements: reqs })}
                />
              </CardContent>
            </Card>

            <Card id="placement" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Slot placement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Auto-place new slots in the binary tree"
                  description="When on, slot creation auto-places under the sponsor via spillover (legacy behavior). When off (default), slots are created unplaced and the owner picks an exact L/R seat from the genealogy view. Explicit placement in the create body always wins regardless of this flag."
                  value={draft.auto_place_slots}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, auto_place_slots: v })}
                />
                <Toggle
                  label="Let members choose placement when redeeming"
                  description='When on, the member redeem/activation form shows a "Place in my binary tree now" checkbox with an anchor + L/R picker (only once the member already owns a slot). When off (default), the checkbox is hidden and placement follows the Auto-place setting above. UI only — the server still honors any explicit placement it receives.'
                  value={draft.manual_placement_on_redeem}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, manual_placement_on_redeem: v })}
                />
                <Toggle
                  label='Show "Activate New Slot" on the genealogy tree'
                  description="When on (default), the genealogy tree shows the Activate/Add New Slot action card. When off, that entry point is hidden — members can still redeem codes from the Code Vault."
                  value={draft.genealogy_activate_slot}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, genealogy_activate_slot: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Slot limit per member</label>
                  <Input
                    type="number"
                    min={1}
                    value={draft.max_slots_per_member ?? ''}
                    disabled={!canWrite}
                    placeholder="Unlimited"
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      setDraft({
                        ...draft,
                        max_slots_per_member: v === '' ? null : Math.max(1, Math.floor(Number(v))),
                      });
                    }}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum number of slots a single member may own. Leave blank for{' '}
                    <strong>unlimited</strong>. Enforced when a new slot is created; the member area
                    shows each member their <strong>current / limit</strong> count.
                  </p>
                </div>
                <Toggle
                  label="Paid upgrades re-fire activation bonuses"
                  description="When on, upgrading an already-paid slot to a higher tier re-fires activation comp (sign-up bonuses, etc.) for the new tier. When off (default), a paid-to-paid upgrade is just a tier change. A free-to-paid upgrade always fires activation regardless of this flag."
                  value={draft.paid_upgrade_refires_bonuses}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, paid_upgrade_refires_bonuses: v })}
                />
                <Toggle
                  label="Number slot codes per member"
                  description="When on, each new slot's code keeps its random base and gets a per-member ordinal suffix (e.g. SK8N3PQRX7M-01, then -02). When off (default), codes are purely random. Applies only to slots created after enabling — existing codes are not renumbered."
                  value={draft.slot_code_series.enabled}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      slot_code_series: { ...draft.slot_code_series, enabled: v },
                    })
                  }
                />
              </CardContent>
            </Card>

            <Card id="genealogy-details" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Genealogy box details (binary)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Which details show on each box of the <strong>binary</strong> genealogy tree, for
                  both members and admins. Rank, package, and currency always show.
                </p>
                <Toggle
                  label="Full name"
                  description="The slot owner's name."
                  value={draft.binary_genealogy_node_fields.fullName}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        fullName: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Slot code"
                  value={draft.binary_genealogy_node_fields.slotCode}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        slotCode: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Package amount"
                  description="The membership/kit price (e.g. ₱23,400.00) + its currency. Off by default — it's the same for everyone in a tier."
                  value={draft.binary_genealogy_node_fields.packageAmount}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        packageAmount: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Current binary points (Left)"
                  description="The slot's current standing points on its left leg."
                  value={draft.binary_genealogy_node_fields.leftPoints}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        leftPoints: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Current binary points (Right)"
                  description="The slot's current standing points on its right leg."
                  value={draft.binary_genealogy_node_fields.rightPoints}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        rightPoints: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Sponsor name"
                  description="The owner of this slot's sponsor slot."
                  value={draft.binary_genealogy_node_fields.sponsorName}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        sponsorName: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Sponsor slot code"
                  value={draft.binary_genealogy_node_fields.sponsorSlotCode}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        sponsorSlotCode: v,
                      },
                    })
                  }
                />
                <Toggle
                  label="Wallet balance"
                  description="The slot's current cash wallet balance. Off by default — when on, members browsing their own genealogy see their downlines' balances too, not just admins."
                  value={draft.binary_genealogy_node_fields.walletBalance}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      binary_genealogy_node_fields: {
                        ...draft.binary_genealogy_node_fields,
                        walletBalance: v,
                      },
                    })
                  }
                />
              </CardContent>
            </Card>

            <Card id="volume-pv" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Volume &amp; PV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Count personal PV in group PV"
                  description="When on (default), a member's own purchases count toward their OWN group volume (GPV = personal PV + downline PV). When off, GPV reflects only downline volume — a member's own purchase raises their PPV but not their GPV. Affects every GPV-based requirement, e.g. Stairstep rank min GPV. Upline group volume is unaffected either way. Applies going forward, from the next purchase."
                  value={draft.gpv_includes_ppv}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, gpv_includes_ppv: v })}
                />
                <Toggle
                  label="Product Code Generation (defer PV to a redeemable code)"
                  description="When on, a purchase does NOT grant PV immediately. Instead it mints a PV-carrying 'product code' into the buyer's Code Vault; the PV (PPV/GPV + any rank-up) is released only when the buyer redeems that code. Commissions still pay at purchase — only the PV volume is deferred. Membership Kit orders are unaffected (they mint their own activation codes). Off by default."
                  value={draft.product_code_generation}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, product_code_generation: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Member PV monitoring (dashboard)</label>
                  <p className="text-xs text-muted-foreground">
                    Shows members a per-month PPV/GPV breakdown card on their dashboard.
                    Display-only — does not affect any computation.
                  </p>
                  <Select
                    value={draft.member_pv_display}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        member_pv_display: v as TenantFeatures['member_pv_display'],
                      })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ppv_and_gpv">Personal &amp; Group PV (default)</SelectItem>
                      <SelectItem value="ppv_only">Personal PV only</SelectItem>
                      <SelectItem value="off">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card id="stairstep" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Stairstep ranks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Live rank-up"
                  description="When on (default), a slot's Stairstep rank is recomputed immediately on slot activation and product repurchase, so the rank — and the rank-based product pricing that follows it — updates right away. When off, rank only advances at period close. Ranks are monotonic either way: a recompute can only raise a rank, never lower it."
                  value={draft.stairstep_live_rankup}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, stairstep_live_rankup: v })}
                />
                <Toggle
                  label="Pay one-time rank-up bonus"
                  description="When on, reaching a new (higher) rank pays that rank's Reward cash / Reward GC (set per rank in the ComPlan Stairstep editor). Paid once per rank for the slot's lifetime. Off by default."
                  value={draft.stairstep_rankup_bonus}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, stairstep_rankup_bonus: v })}
                />
                <Toggle
                  label="Notify on reaching the first rank"
                  description="When on (default), every rank-up sends the member a “You reached …” notification, including the first rank. Turn it off when the first rank needs nothing (e.g. a Default Rank everyone holds), so members are only told about ranks they worked for."
                  value={draft.rank_up_notify_entry_rank}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, rank_up_notify_entry_rank: v })}
                />
                <Toggle
                  label="Exclude personal purchases from commission"
                  description="When on, a member's OWN purchases don't earn them Stairstep commission — only their group's (downline) volume does. When off (default), a member also earns their rank % on their own personal PV. This changes only the payout base; rank qualification is unaffected."
                  value={draft.stairstep_exclude_personal_pv}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, stairstep_exclude_personal_pv: v })}
                />
                <Toggle
                  label="Show ranks showcase"
                  description="When on, the ranks and their icons show on the storefront landing page and the member dashboard, together with who achieved each rank (names are masked, e.g. Juan D.). Set each rank's display name, description and icon in the ComPlan Stairstep editor. Off by default."
                  value={draft.rank_showcase_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, rank_showcase_enabled: v })}
                />
              </CardContent>
            </Card>

            <Card id="cd-slots" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>CD / Half-CD slots</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  A <strong>CD (Commission Deductible)</strong> kit mints a slot that enters like a
                  Free slot (no comp fires for its uplines) but carries a <strong>debt</strong> —
                  set per kit on the product editor. The slot earns normally, but its{' '}
                  <strong>cash</strong> earnings pay the debt off first; once fully settled, the
                  slot becomes a Paid slot and its uplines receive the deferred activation comp
                  (direct, binary placement, etc.) at that moment. <strong>Half-CD</strong> is the
                  same except only the plans checked below pay the debt — every other plan's
                  earnings reach the wallet immediately. GC / coins / tokens are never deducted.
                  Admins can adjust or forgive a debt from the slot's detail page.
                </p>
                <Toggle
                  label="In-debt slots' purchases pay upline commissions"
                  description="When on (default), an in-debt CD slot's own purchases fire order commissions (unilevel, binary points, …) for its uplines like any slot. When off, those commissions are skipped while the debt is open — volume/PV still rolls up — and they are NOT paid retroactively at settlement."
                  value={draft.cd_orders_pay_upline}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cd_orders_pay_upline: v })}
                />
                <Toggle
                  label="In-debt CD slots count for Stairstep requirements"
                  description="When on (default), an in-debt CD slot counts toward its sponsor's Stairstep direct/leg requirements immediately. When off, it only counts after its debt is settled. Group PV contribution is unaffected either way."
                  value={draft.cd_stairstep_count_immediately}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cd_stairstep_count_immediately: v })}
                />
                <Toggle
                  label="In-debt Half-CD slots count for Stairstep requirements"
                  description="Same as above, but for Half-CD slots — the two types have independent settings."
                  value={draft.half_cd_stairstep_count_immediately}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, half_cd_stairstep_count_immediately: v })}
                />
                <Toggle
                  label="In-debt CD slots count for the Binary Mentor's Bonus"
                  description="When on (default), an in-debt CD/Half-CD direct counts toward a mentor's per-level min-directs requirement for the Binary Mentor's Bonus, like any active direct. When off, in-debt CD/Half-CD directs are ignored for that requirement until the debt settles (the slot flips to Paid). Applies to the binary plan's mentor rider, not the standalone mentor bonus."
                  value={draft.cd_binary_mentor_count_directs}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cd_binary_mentor_count_directs: v })}
                />
                <Toggle
                  label="In-debt CD slots count for Extreme Binary preferred placement"
                  description="When on (default), an in-debt CD/Half-CD direct counts toward the direct requirement that unlocks changing a slot's L/R spillover side (Extreme Binary). When off, in-debt CD/Half-CD directs don't count until the debt settles. No effect on non-Extreme binary variants."
                  value={draft.cd_binary_placement_count_directs}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cd_binary_placement_count_directs: v })}
                />
                <Toggle
                  label="Half-CD triggers comp plans like a Paid slot"
                  description="When on, redeeming a Half-CD kit fires activation/placement comp for the uplines immediately (like a Paid slot); settling the debt then changes nothing except stopping the deduction. When off (default), Half-CD defers its entry comp to settlement exactly like CD. Snapshotted per slot at redemption — flipping this later never double-pays."
                  value={draft.half_cd_triggers_like_ps}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, half_cd_triggers_like_ps: v })}
                />
                <Toggle
                  label="Block cash-outs from in-debt CD slots"
                  description="When on (default), a CD slot with an open debt cannot request a cash payout — its cash earnings garnish into the debt first. Half-CD slots are always allowed to cash out (only their debt-paying plans garnish, so other earnings are legitimately withdrawable). A settled slot is already a Paid slot and is never blocked."
                  value={draft.cd_block_payout_in_debt}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cd_block_payout_in_debt: v })}
                />
                <Toggle
                  label="Let members settle a CD debt by topping up"
                  description="When on (default), an approved member top-up to an in-debt CD/Half-CD slot pays that slot's debt first (the deposit garnishes into the debt like the slot's earnings do); any amount above the remaining debt lands in the wallet. This is the member's self-serve payoff — it applies to both CD and Half-CD regardless of the debt-paying-plans list above. When off, top-ups always credit the wallet."
                  value={draft.cd_topup_settles_debt}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, cd_topup_settles_debt: v })}
                />
                {draft.cd_topup_settles_debt && (
                  <div className="pl-1 space-y-1">
                    <label className="text-xs font-medium">
                      Top-up bonus on a debt-paying top-up
                    </label>
                    <Select
                      value={draft.cd_topup_bonus_treatment}
                      disabled={!canWrite}
                      onValueChange={(v) =>
                        setDraft({
                          ...draft,
                          cd_topup_bonus_treatment: v as typeof draft.cd_topup_bonus_treatment,
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="principal_only">
                          Principal only pays the debt (bonus credits the wallet)
                        </SelectItem>
                        <SelectItem value="include_cash_bonus">
                          Principal + cash bonus pay the debt (GC bonus stays in the wallet)
                        </SelectItem>
                        <SelectItem value="no_bonus">No bonus on a debt-paying top-up</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      Only affects top-ups that land on a slot with an open debt. Ordinary wallet
                      top-ups always earn their bonus.
                    </p>
                  </div>
                )}
                <div className="border-t pt-4 space-y-2">
                  <p className="text-sm font-medium">Half-CD debt-paying plans</p>
                  <p className="text-xs text-muted-foreground">
                    Cash earnings from the checked plans pay an in-debt Half-CD slot's debt;
                    unchecked plans credit the wallet normally. With nothing checked, a Half-CD debt
                    can only be settled by an admin adjustment. CD slots ignore this list — every
                    plan pays their debt.
                  </p>
                  <div className="space-y-2 pl-1">
                    {[
                      ...SUPPORTED_PLAN_CODES.map((code) => ({
                        code,
                        label: planDisplayName(code),
                      })),
                      // Extra garnishable KINDS that live under a shared source plan
                      // (e.g. Binary Mentor Bonus, under `binary`) but get their own
                      // independent debt-paying toggle.
                      ...GARNISH_EXTRA_KINDS.map((k) => ({
                        code: k.key as string,
                        label: k.label,
                      })),
                    ].map(({ code, label }) => {
                      const on = (draft.half_cd_debt_plans ?? []).includes(code);
                      return (
                        <Toggle
                          key={code}
                          label={label}
                          value={on}
                          disabled={!canWrite}
                          onChange={(next) => {
                            const cur = new Set(draft.half_cd_debt_plans ?? []);
                            if (next) cur.add(code);
                            else cur.delete(code);
                            setDraft({ ...draft, half_cd_debt_plans: Array.from(cur).sort() });
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="pricing" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Pricing & fulfillment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Ecom comp event fires on</label>
                  <Select
                    value={draft.ecom_event_on}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        ecom_event_on: v as TenantFeatures['ecom_event_on'],
                      })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="payment_approved">Payment approved (default)</SelectItem>
                      <SelectItem value="order_completed">Order completed (delivery)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Determines when ecom order commissions fire. POS orders always fire at checkout.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Kit Code &amp; PIN reveal</label>
                  <Select
                    value={draft.kit_code_reveal_on}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        kit_code_reveal_on: v as TenantFeatures['kit_code_reveal_on'],
                      })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="payment_approved">When payment is approved</SelectItem>
                      <SelectItem value="shipped">When the order ships</SelectItem>
                      <SelectItem value="delivered">When the order is delivered</SelectItem>
                      <SelectItem value="completed">
                        When the order is completed (default)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    When ecom kit Code &amp; PIN unlocks in the member's Vault. Earlier reveal =
                    faster activation, but{' '}
                    <strong>redeeming a code makes that line item non-refundable</strong>
                    even if the kit hasn't physically arrived yet. Wallet-instant orders always mint
                    codes at checkout regardless of this setting.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Auto-complete orders after (days)</label>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={draft.order_auto_complete_days ?? ''}
                    disabled={!canWrite}
                    placeholder="Off"
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      setDraft({
                        ...draft,
                        order_auto_complete_days:
                          v === '' ? null : Math.max(0, Math.floor(Number(v))),
                      });
                    }}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Orders waiting for the member to confirm receipt (delivered or ready-for-pickup)
                    auto-complete after this many days. Leave blank or set <strong>0</strong> to
                    keep them open until the member taps "Mark received".
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Auto-cancel unpaid orders after (days)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={draft.order_auto_cancel_unpaid_days ?? ''}
                    disabled={!canWrite}
                    placeholder="Off"
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      setDraft({
                        ...draft,
                        order_auto_cancel_unpaid_days:
                          v === '' ? null : Math.max(0, Math.floor(Number(v))),
                      });
                    }}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Unpaid orders are cancelled after this many days. It <strong>never</strong>{' '}
                    touches an order with a payment awaiting your review, one whose online payment
                    is still in progress, or one still waiting for you to set a shipping fee — the
                    clock also restarts whenever you reject a payment, so a member re-uploading
                    proof gets a fresh window. Leave blank or set <strong>0</strong> to disable.
                  </p>
                </div>
                {/* Master switch for the whole virtual-products feature. The two
              policy toggles it governs (this one and "open their replicated
              page" further down) are hidden while it's off, so a tenant that
              doesn't sell services never reads settings about them. */}
                <Toggle
                  label="Enable virtual products"
                  description="When on, the admin gains a Virtual Products section for services and digital goods — a separate catalog, a 'Virtual product / service' flag on the product editor, and the virtual-order policies below. Off (default) hides all of it. This only affects what the admin shows: a product already marked virtual keeps skipping shipping, dimensions and inventory either way, and stays editable in the ordinary Products list while this is off."
                  value={draft.virtual_products_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, virtual_products_enabled: v })}
                />
                {draft.virtual_products_enabled && (
                  <Toggle
                    label="Complete virtual-only orders once paid"
                    description="When on (default), an ecom order whose every line is a Virtual product/service completes the moment payment is confirmed — there is nothing to ship or hand over. Mixed carts (virtual + physical), branch-pickup orders, and Cash-on-Delivery orders always follow the normal ladder. Note: with 'Ecom comp event' set to Order completed, commissions on these orders now land at payment; and with 'Kit code reveal' set to Completed, a virtual kit's Code+PIN unlocks at payment."
                    value={draft.virtual_orders_auto_complete}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, virtual_orders_auto_complete: v })}
                  />
                )}
                {draft.virtual_products_enabled && (
                  <Toggle
                    label="Split mixed carts into separate virtual and physical orders"
                    description="When on, a cart holding BOTH virtual and physical items becomes two orders the buyer pays for in one go: a physical order that ships normally, and a virtual order with no delivery that completes as soon as payment clears (so a course isn't held up by a t-shirt). One payment, one proof, two orders shown together. Carts with only one kind are never split, and Cash-on-Delivery orders always stay a single order. Off by default."
                    value={draft.virtual_orders_split_checkout}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, virtual_orders_split_checkout: v })}
                  />
                )}
                <Toggle
                  label="Enable materials and recipes"
                  description="When on, the admin gains a Materials page for supplies that are stocked but never sold (cups, lids, ingredients), and each product gets a Recipe tab listing the materials one sold unit uses up. Selling the product then takes those materials off the branch's shelf. Running out of a material never blocks a sale: the count stops at zero and the shortfall shows in the stock log. Off (default) hides all of it. This only affects what the admin shows: a recipe that already exists keeps using up its materials either way."
                  value={draft.materials_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, materials_enabled: v })}
                />
                <Toggle
                  label="Track stock in lots"
                  description="When on, every delivery you receive becomes its own lot with the cost you paid and, if you type one, an expiry date. Sales then take the oldest stock first, each sale records which lots it used, and its cost of goods is what those units actually cost instead of the product's fixed cost. Adds the lot fields to the receive form, a Lots view on each branch, and an Expiring Stock page. Stock already on the shelf when you turn this on has no lot: it goes out first, at the product's usual cost. Off by default. Turn it on once and leave it on; while it is off no lot is recorded, so lots saved earlier stop matching the shelf."
                  value={draft.inventory_lots_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, inventory_lots_enabled: v })}
                />
                {draft.inventory_lots_enabled && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Which lot goes out first</label>
                    <Select
                      value={draft.lot_pick_order ?? 'fifo'}
                      disabled={!canWrite}
                      onValueChange={(v) =>
                        setDraft({
                          ...draft,
                          lot_pick_order: v as TenantFeatures['lot_pick_order'],
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fifo">Oldest received first (default)</SelectItem>
                        <SelectItem value="fefo">Soonest to expire first</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Oldest received first suits most stock. Soonest to expire first is for goods
                      where a later delivery can carry an earlier date; lots with no date still go
                      out in the order they were received.
                    </p>
                  </div>
                )}
                <Toggle
                  label="Item locator"
                  description="When on, each branch's stock list gets a Location for every product (an aisle or area, then a rack or shelf), and the cashier's Inventory page shows it so staff can find the item. Off (default) hides it; a location already saved is kept and comes back when you turn this on again."
                  value={draft.item_locator_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, item_locator_enabled: v })}
                />
                {SHOW_TENANT_NON_ACTIVATED_PICKER && (
                  <NonActivatedPricingPicker
                    value={draft.non_activated_pricing_membership_id}
                    memberships={membershipsQuery.data ?? []}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, non_activated_pricing_membership_id: v })}
                  />
                )}
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    Discount source for members with several slots
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Which slot's price a member pays when they hold slots at more than one
                    membership tier. <strong>Selected slot</strong> (default) charges the tier of
                    the slot they are logged in on, so switching slots re-prices the store.{' '}
                    <strong>Best across all slots</strong> always charges the lowest price any of
                    their active slots qualifies for. Either way commissions, PV and order
                    attribution follow the <em>selected</em> slot, and stockist / branch prices are
                    never affected.
                  </p>
                  <Select
                    value={draft.pricing_slot_source}
                    disabled={!canWrite}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        pricing_slot_source: v as TenantFeatures['pricing_slot_source'],
                      })
                    }
                  >
                    <SelectTrigger className="h-9 max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active_slot">Currently selected slot (default)</SelectItem>
                      <SelectItem value="best_across_slots">
                        Best price across all their slots
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Toggle
                  label="Commissions on non-cash (GC / coin) purchases"
                  description="When off (default), an order paid in GC or coins fires NO comp: no commissions, points, tokens, or coin-grants (scrip purchases don't pay uplines). When on, a non-cash purchase pays uplines exactly like a cash sale. Cash orders are never affected."
                  value={draft.commissions_on_non_cash}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, commissions_on_non_cash: v })}
                />
                <Toggle
                  label="Charge shipping on wallet (GC / coin) orders"
                  description="On by default. A wallet order WITH a delivery address is quoted shipping like a cash order — the fee is added to the wallet debit in that order's own currency (1 GC = ₱1) — and then follows the normal fulfilment ladder (Ready to Ship → Shipping → Delivered → Completed). Turn it OFF and a wallet-paid order can't carry shipping and completes the instant it is placed — which leaves nothing to pick or hand over if the goods are physical. Pickup orders are unaffected either way and still complete instantly. Note: with this on, kit codes and commissions follow the 'Kit code reveal' and 'Ecom comp event' settings above rather than firing at checkout."
                  value={draft.wallet_orders_charge_shipping}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, wallet_orders_charge_shipping: v })}
                />
                <Toggle
                  label="Auto-complete wallet (GC / coin) orders at checkout"
                  description="OFF by default, and normally leave it off. Paying from a wallet settles the money, not the sale — the buyer still has to collect the goods. Off, a wallet order for physical items stops at Paid and walks the normal pickup ladder (Ready for Pickup → Completed) like a cash sale, so the counter has a worklist and the member can see what is waiting. On, it is marked Completed the moment it is placed, while the box is still on the shelf. Purely digital orders ignore this and always complete at checkout — a course has nothing to hand over."
                  value={draft.wallet_orders_auto_complete}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, wallet_orders_auto_complete: v })}
                />
                {draft.virtual_products_enabled && (
                  <Toggle
                    label="Virtual products open their replicated page"
                    description="When on, clicking a Virtual product in the online store opens the Replicated page assigned to it (set per product in Products → Replicated page) instead of the normal product page — so a service sells through its campaign landing page. Only virtual products with an ENABLED page assigned are affected; everything else keeps the product page. The product's own URL (/product/…) is never redirected, so shared and dropship links still work. Needs the replicated-page feature switched on, and the assigned page itself enabled — with either off the link falls back to the product page and nothing appears to happen."
                    value={draft.virtual_products_open_replicated_page}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({ ...draft, virtual_products_open_replicated_page: v })
                    }
                  />
                )}
                <Toggle
                  label="Pay immediately for courses / services"
                  description="When on, a cart holding ONLY virtual items (a course, a service, a virtual kit) checks out on a stripped screen with no shop header, and goes straight to the payment page once the order is placed — no success dialog to click through. Such a cart already has nothing to fill in: no address, no courier, no shipping fee. Off by default; it changes where every buyer of a virtual item lands, not only campaign traffic. Which payment methods are offered there is set per method in Ecom payment methods."
                  value={draft.virtual_instant_pay}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, virtual_instant_pay: v })}
                />
                <Toggle
                  label="Hold unpaid campaign signups on the payment page"
                  description="When on, a member who registered through a campaign / replicated page and has an unpaid order is sent to the payment page from anywhere in the portal until they pay. Members who joined through an ordinary referral link (the QR one) are never held — they keep the reminder dialog instead. An order still waiting for you to set its manual shipping fee never holds anyone, since it can't be paid yet. Off by default. Pair it with 'Let members cancel their own unpaid orders' below, or an abandoned order keeps that member on the payment page until you cancel it for them."
                  value={draft.unpaid_order_lock_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, unpaid_order_lock_enabled: v })}
                />
                <Toggle
                  label="Let members cancel their own unpaid orders"
                  description="When on, a member can cancel an order of theirs that hasn't been paid yet. It never appears once money is involved: an order whose payment proof is waiting for your review, or whose online payment is still going through, can't be cancelled by the member — they're told you're confirming a payment instead. Cancelling one order of a multi-shop checkout cancels all of them, since the buyer pays for the whole group at once. Off by default."
                  value={draft.member_order_cancel_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, member_order_cancel_enabled: v })}
                />
                <Toggle
                  label="Resume a virtual checkout after re-login"
                  description="When on, a member who logs out mid-purchase is taken back to where they left off the next time they log in: an unpaid virtual-only order sends them straight to its payment page; otherwise a cart holding only virtual items sends them to checkout. One-time redirect at login only — it never locks anyone anywhere, and a login link that names its own destination still wins. Off by default."
                  value={draft.virtual_checkout_resume_on_login}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, virtual_checkout_resume_on_login: v })}
                />
                <Toggle
                  label="Online payment gateways (Dragonpay / iPay88)"
                  description="Master switch for online gateway checkout. When off (default), a 'gateway' payment method can't transact even if enabled in the registry — the initiate route refuses. Turn on only once the server has the gateway's credentials in its .env."
                  value={draft.gateway_payments_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, gateway_payments_enabled: v })}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Coin name</label>
                  <Input
                    value={draft.coin_label ?? ''}
                    disabled={!canWrite}
                    placeholder="Coins"
                    onChange={(e) => setDraft({ ...draft, coin_label: e.target.value })}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Display name for the coin currency shown across the apps (e.g. "Gems", "Reward
                    Points").
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card id="compplans" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Comp plans</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Disable plans your tenant doesn't run. The worker skips disabled plans on every
                  event; their compute paths never execute. Configs stay in place so re-enabling is
                  instant. Plans are grouped by <strong>when they fire</strong>; edit per-plan
                  config under <code className="font-mono">Compensation → ComPlan configs</code>.
                </p>
                <p className="text-xs text-muted-foreground">
                  <strong>Daily cap</strong> = the global per-plan maximum cash income a slot can
                  earn from that plan per day (blank = no limit). A membership tier can override it
                  (Members → Memberships). Binary is exempt; it caps via its own pairing settings.
                </p>
                <p className="text-xs text-muted-foreground">
                  <strong>{coinLabel} reward</strong> = a flat {coinLabel.toLowerCase()} amount paid
                  to each earner of that plan, alongside their cash (blank = none). Free slots earn
                  no {coinLabel.toLowerCase()} from this rider.
                </p>
                <Toggle
                  label="Free slots earn commissions"
                  description="When on (default), a free slot receives commissions from its downlines like any paid slot. When off, the engine drops any commission whose recipient is a free slot (they stay non-earning). Either way, creating a free slot fires no upline comp."
                  value={draft.free_slots_earn}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, free_slots_earn: v })}
                />
                {TRIGGER_ORDER.map((trigger) => {
                  const group = SUPPORTED_PLAN_CODES.filter(
                    (code) => planTrigger(code) === trigger,
                  );
                  if (group.length === 0) return null;
                  return (
                    <div key={trigger} className="space-y-2">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        {TRIGGER_LABEL[trigger]}
                      </p>
                      <div className="space-y-2 pl-1">
                        {group.map((code) => {
                          const isOff = (draft.disabled_plans ?? []).includes(code);
                          const stored = latestConfigByPlan.get(code);
                          const variantId =
                            (stored?.config as { variant_id?: string } | undefined)?.variant_id ??
                            null;
                          const capValue = draft.daily_caps?.[code];
                          return (
                            <div key={code} className="flex items-center gap-2">
                              <Toggle
                                label={planDisplayName(code)}
                                value={!isOff}
                                disabled={!canWrite}
                                onChange={(on) => {
                                  const current = new Set(draft.disabled_plans ?? []);
                                  if (on) current.delete(code);
                                  else current.add(code);
                                  setDraft({
                                    ...draft,
                                    disabled_plans: Array.from(current).sort(),
                                  });
                                }}
                              />
                              {variantId ? (
                                <Badge variant="muted" title="Active variant">
                                  variant: {variantId}
                                </Badge>
                              ) : null}
                              {code === 'binary' ? null : (
                                <Input
                                  type="number"
                                  min={0}
                                  step="any"
                                  className="h-7 w-28 text-xs"
                                  placeholder="No daily cap"
                                  disabled={!canWrite}
                                  value={capValue ?? ''}
                                  onChange={(e) => {
                                    const caps = { ...(draft.daily_caps ?? {}) };
                                    const raw = e.target.value;
                                    if (raw === '') delete caps[code];
                                    else caps[code] = Number(raw);
                                    setDraft({ ...draft, daily_caps: caps });
                                  }}
                                />
                              )}
                              <Input
                                type="number"
                                min={0}
                                step="any"
                                className="h-7 w-28 text-xs"
                                placeholder={`No ${coinLabel.toLowerCase()}`}
                                title={`${coinLabel} reward per earner`}
                                disabled={!canWrite}
                                value={draft.coin_rewards?.[code] ?? ''}
                                onChange={(e) => {
                                  const rewards = { ...(draft.coin_rewards ?? {}) };
                                  const raw = e.target.value;
                                  if (raw === '') delete rewards[code];
                                  else rewards[code] = Number(raw);
                                  setDraft({ ...draft, coin_rewards: rewards });
                                }}
                              />
                              {draft.assistant.credits_enabled && (
                                <Input
                                  type="number"
                                  min={0}
                                  step={1}
                                  className="h-7 w-28 text-xs"
                                  placeholder="No answers"
                                  title="AI answers per earner"
                                  disabled={!canWrite}
                                  value={draft.ai_credit_rewards?.[code] ?? ''}
                                  onChange={(e) => {
                                    const rewards = { ...(draft.ai_credit_rewards ?? {}) };
                                    const raw = e.target.value;
                                    if (raw === '') delete rewards[code];
                                    else rewards[code] = Math.max(0, Math.floor(Number(raw)));
                                    setDraft({ ...draft, ai_credit_rewards: rewards });
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div className="border-t pt-4 space-y-2">
                  <p className="text-sm font-medium">Distribution-run-only plans</p>
                  <p className="text-xs text-muted-foreground">
                    Plans paid <strong>only</strong> via an ad-hoc Distribution Run (Compensation →
                    Distribution Runs), never at the monthly period close. Enable for a plan you
                    distribute on its own date window so the close doesn't <em>also</em> pay it —
                    which would double-pay the overlapping window. Realtime accumulation (e.g.
                    Binary leg points) is unaffected.
                  </p>
                  <div className="space-y-2 pl-1">
                    {(
                      [
                        'monthly_direct_bonus',
                        'binary',
                        'stairstep',
                        'unilevel_consecutive_bonus',
                        'maintenance_cashback',
                      ] as const
                    ).map((code) => {
                      const on = (draft.distribution_run_only_plans ?? []).includes(code);
                      return (
                        <Toggle
                          key={code}
                          label={planDisplayName(code)}
                          value={on}
                          disabled={!canWrite}
                          onChange={(next) => {
                            const cur = new Set(draft.distribution_run_only_plans ?? []);
                            if (next) cur.add(code);
                            else cur.delete(code);
                            setDraft({
                              ...draft,
                              distribution_run_only_plans: Array.from(cur).sort(),
                            });
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="border-t pt-4 space-y-2">
                  <p className="text-sm font-medium">Company-slot auto-maintained plans</p>
                  <p className="text-xs text-muted-foreground">
                    For the checked plans, <strong>Company slots</strong> are treated as maintained
                    automatically — they skip that plan's monthly personal-PV (maintenance)
                    requirement and always qualify. Other slots still need to maintain. Only plans
                    that have a maintenance gate are listed.
                  </p>
                  <div className="space-y-2 pl-1">
                    {(
                      [
                        'stairstep',
                        'unilevel',
                        'maintenance_cashback',
                        'monthly_direct_bonus',
                      ] as const
                    ).map((code) => {
                      const on = (draft.company_auto_maintained_plans ?? []).includes(code);
                      return (
                        <Toggle
                          key={code}
                          label={planDisplayName(code)}
                          value={on}
                          disabled={!canWrite}
                          onChange={(next) => {
                            const cur = new Set(draft.company_auto_maintained_plans ?? []);
                            if (next) cur.add(code);
                            else cur.delete(code);
                            setDraft({
                              ...draft,
                              company_auto_maintained_plans: Array.from(cur).sort(),
                            });
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="border-t pt-4 space-y-1">
                  <p className="text-sm font-medium">Daily income cap groups</p>
                  <p className="text-xs text-muted-foreground">
                    Grouped daily income caps are now configured{' '}
                    <strong>per membership tier</strong> (Members → Memberships → edit a tier), so
                    each tier can group its own plans under its own shared daily limit.
                  </p>
                </div>
                <div className="border-t pt-4 space-y-1">
                  <p className="text-sm font-medium">Unilevel distribution</p>
                  <p className="text-xs text-muted-foreground">
                    Unilevel is <strong>always admin-triggered</strong> — it never pays
                    automatically when a purchase completes. Purchase PV accumulates over the
                    period, and an admin releases the whole period at once from{' '}
                    <strong>Compensation → Distribute → Unilevel</strong> (review qualifiers →
                    preview → distribute). This is a deliberate monthly manual payout, unlike
                    Referral and Binary, which pay in real time as purchases complete.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card id="labels" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Ledger labels</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Friendly display names for ledger entries, shown to members in{' '}
                  <strong>Earnings</strong> and across admin wallet views. Leave a field blank to
                  use the built-in default (shown as the placeholder). A type not listed here falls
                  back to a humanized name automatically.
                </p>

                {LEDGER_KIND_GROUPS.map((group) => (
                  <div key={group.label} className="space-y-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                      {group.label}
                    </p>
                    <div className="space-y-1.5 pl-1">
                      {group.kinds.map((kind) => (
                        <LabelInputRow
                          key={kind}
                          token={kind}
                          placeholder={LEDGER_KIND_LABEL_DEFAULTS[kind] ?? kind}
                          disabled={!canWrite}
                          value={draft.ledger_labels?.[kind] ?? ''}
                          onChange={(raw) => {
                            const next = { ...(draft.ledger_labels ?? {}) };
                            if (raw.trim() === '') delete next[kind];
                            else next[kind] = raw;
                            setDraft({ ...draft, ledger_labels: next });
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Source plans
                  </p>
                  <div className="space-y-1.5 pl-1">
                    {LEDGER_SOURCE_PLAN_CODES.map((plan) => (
                      <LabelInputRow
                        key={plan}
                        token={plan}
                        placeholder={LEDGER_SOURCE_PLAN_LABEL_DEFAULTS[plan] ?? plan}
                        disabled={!canWrite}
                        value={draft.ledger_source_plan_labels?.[plan] ?? ''}
                        onChange={(raw) => {
                          const next = { ...(draft.ledger_source_plan_labels ?? {}) };
                          if (raw.trim() === '') delete next[plan];
                          else next[plan] = raw;
                          setDraft({ ...draft, ledger_source_plan_labels: next });
                        }}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="earnings-order" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Earnings breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Arrange, rename, and show/hide the cards in the member{' '}
                  <strong>Earnings Breakdown</strong> panel. Each row is one earnings bucket: type a
                  display label (blank = default), then pick when it shows —{' '}
                  <strong>Always show</strong> renders the card even at zero earnings,{' '}
                  <strong>Show when earned</strong> only once the member has credits (the classic
                  behavior), <strong>Hidden</strong> suppresses it. Disabled plans never appear.
                  Labels here are shared with the member passbook, notifications, and admin wallet
                  views — the <strong>Ledger labels</strong> card edits the same values.
                </p>
                <EarningsBreakdownRowsEditor
                  rows={draft.earnings_breakdown_rows ?? []}
                  legacyOrder={draft.earnings_breakdown_order ?? []}
                  disabledPlans={draft.disabled_plans ?? []}
                  planLabels={draft.ledger_source_plan_labels ?? {}}
                  kindLabels={draft.ledger_labels ?? {}}
                  disabled={!canWrite}
                  onChange={(next) => setDraft({ ...draft, earnings_breakdown_rows: next })}
                  onLabelChange={(key, raw) => {
                    // Sub-bucket rows label through the ledger KIND map; plan rows
                    // through the SOURCE-PLAN map — same maps as the Ledger labels
                    // card above. Blank falls back to the default (delete the key).
                    const sub = EARNINGS_SUB_BUCKETS.find((b) => b.key === key);
                    if (sub?.kind) {
                      const next = { ...(draft.ledger_labels ?? {}) };
                      if (raw.trim() === '') delete next[sub.kind];
                      else next[sub.kind] = raw;
                      setDraft({ ...draft, ledger_labels: next });
                    } else {
                      const next = { ...(draft.ledger_source_plan_labels ?? {}) };
                      if (raw.trim() === '') delete next[key];
                      else next[key] = raw;
                      setDraft({ ...draft, ledger_source_plan_labels: next });
                    }
                  }}
                />
              </CardContent>
            </Card>

            <Card id="wallet-topup" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Wallet top-up</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Allow members to top up their wallet"
                  description="Members submit an amount + proof of payment; an admin approves it and the slot wallet is credited."
                  value={draft.wallet_topup_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, wallet_topup_enabled: v })}
                />
                <Toggle
                  label="Allow top-ups at the till"
                  description="Cashiers with the till top-up permission can load a member's cash wallet at the counter, paid in cash: it goes in the drawer and shift close expects it. It is approved on the spot and earns the same bonus brackets and gift checks as an approved request. Not offered at stockist branches. With Accounting on, give each till a Cash/Bank rule for “Wallet top-ups at the till”, as you did for POS sales, or the top-ups miss the till's register."
                  value={draft.pos_topup_enabled ?? false}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, pos_topup_enabled: v })}
                />
                {draft.pos_topup_enabled && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="topup-pin-above">
                      Manager PIN for till top-ups from
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">₱</span>
                      <Input
                        id="topup-pin-above"
                        inputMode="decimal"
                        className="w-40"
                        placeholder="Never"
                        value={draft.pos_topup_pin_above ?? ''}
                        disabled={!canWrite}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            pos_topup_pin_above:
                              e.target.value.trim() === '' ? null : e.target.value,
                          })
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      A till top-up of this amount or more needs a manager to enter their PIN
                      (someone allowed to approve large till top-ups, given to the manager role).
                      The approval is for that member and that amount only. Leave blank to never
                      ask.
                    </p>
                  </div>
                )}
                {draft.pos_topup_enabled && (
                  <Toggle
                    label="Also take card and e-wallet top-ups at the till"
                    description="Lets a till top-up be paid by the till's exact methods (card, GCash, …), with the payment reference required. Nothing at the till checks these the way the drawer count checks cash, and wallet cash can be cashed out, so reconcile them against your card or e-wallet statements."
                    value={draft.pos_topup_exact_methods_enabled ?? false}
                    disabled={!canWrite}
                    onChange={(v) => setDraft({ ...draft, pos_topup_exact_methods_enabled: v })}
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  Members pay using your <strong>manual</strong> payment methods (GCash, bank
                  transfer, …) configured under{' '}
                  <code className="font-mono">
                    Commerce → Pricing &amp; fulfillment → Payment methods
                  </code>{' '}
                  — the same ones they use at checkout. No separate list to maintain.
                </p>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Bonus brackets</p>
                  <p className="text-xs text-muted-foreground">
                    Reward bigger top-ups. <strong>Every</strong> bracket whose range contains the
                    top-up amount applies (both bounds inclusive; blank max = no upper limit), so a
                    Cash bracket and a GC bracket over the same range both credit. A bonus can land
                    in a different wallet than the top-up (e.g. top up Cash, get GC).
                    {draft.vouchers_enabled &&
                      ' A bracket can also give a gift check: pick a program earned with a top-up (a bracket can carry only that, with a value of 0).'}
                  </p>
                  <WalletTopupBracketsEditor
                    value={draft.wallet_topup_brackets ?? []}
                    disabled={!canWrite}
                    programs={draft.vouchers_enabled ? topupPrograms : null}
                    onChange={(next) => setDraft({ ...draft, wallet_topup_brackets: next })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* JM-EX9: per-member notification templates. The materializer renders
          these when fanning out one notification per realized ledger credit. */}
            <Card id="notifications" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Members get an in-app notification each time they earn from a comp plan. Customize
                  the wording below. Placeholders are substituted at delivery time:{' '}
                  <code className="rounded bg-muted px-1">{'{amount}'}</code>,{' '}
                  <code className="rounded bg-muted px-1">{'{currency}'}</code>,{' '}
                  <code className="rounded bg-muted px-1">{'{source_plan}'}</code>,{' '}
                  <code className="rounded bg-muted px-1">{'{kind}'}</code>,{' '}
                  <code className="rounded bg-muted px-1">{'{trigger_user}'}</code>.
                </p>
                <Toggle
                  label="Enable earnings notifications"
                  description="When on, members see a notification per realized cash / GC / coin / token credit. When off, no rows are written (the bell stays empty)."
                  value={draft.notification_templates?.earnings?.enabled ?? true}
                  disabled={!canWrite}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      notification_templates: {
                        ...(draft.notification_templates ?? {
                          earnings: { enabled: true, title: '', body: '' },
                        }),
                        earnings: {
                          ...(draft.notification_templates?.earnings ?? { title: '', body: '' }),
                          enabled: v,
                        },
                      },
                    })
                  }
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="notif-title">
                    Title template
                  </label>
                  <Input
                    id="notif-title"
                    value={draft.notification_templates?.earnings?.title ?? ''}
                    placeholder="You earned {amount} {currency}"
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        notification_templates: {
                          ...(draft.notification_templates ?? {
                            earnings: { enabled: true, title: '', body: '' },
                          }),
                          earnings: {
                            ...(draft.notification_templates?.earnings ?? {
                              enabled: true,
                              body: '',
                            }),
                            title: e.target.value,
                          },
                        },
                      })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="notif-body">
                    Body template
                  </label>
                  <Textarea
                    id="notif-body"
                    rows={3}
                    value={draft.notification_templates?.earnings?.body ?? ''}
                    placeholder="From {source_plan} triggered by {trigger_user}"
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        notification_templates: {
                          ...(draft.notification_templates ?? {
                            earnings: { enabled: true, title: '', body: '' },
                          }),
                          earnings: {
                            ...(draft.notification_templates?.earnings ?? {
                              enabled: true,
                              title: '',
                            }),
                            body: e.target.value,
                          },
                        },
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    The <code>{'{trigger_user}'}</code> placeholder currently falls back to
                    &quot;—&quot; for most credits — keep it for forward-compat or drop it from your
                    template.
                  </p>
                </div>

                {/* Per-event member notifications (orders, payments, cash-outs, sales). */}
                <div className="space-y-4 border-t pt-4">
                  <div>
                    <h3 className="text-sm font-semibold">Event notifications</h3>
                    <p className="text-xs text-muted-foreground">
                      Fire when the matching event happens. Disabled events send nothing.
                      Placeholders: <code className="rounded bg-muted px-1">{'{order_code}'}</code>,{' '}
                      <code className="rounded bg-muted px-1">{'{amount}'}</code>,{' '}
                      <code className="rounded bg-muted px-1">{'{currency}'}</code>,{' '}
                      <code className="rounded bg-muted px-1">{'{method}'}</code>,{' '}
                      <code className="rounded bg-muted px-1">{'{reason}'}</code>,{' '}
                      <code className="rounded bg-muted px-1">{'{product}'}</code>.
                    </p>
                  </div>
                  {CORE_NOTIFICATION_EVENT_CODES.map((code) => (
                    <EventTemplateEditor
                      key={code}
                      code={code}
                      draft={draft}
                      setDraft={setDraft}
                      canWrite={canWrite}
                    />
                  ))}
                </div>
                {/* The LMS templates moved to Learning → Learning Settings,
                    beside the settings they belong to. The pointer stays behind
                    the same gate as the Learning card: the master switch's
                    contract is that a tenant admin on a non-LMS tenant never
                    learns the feature exists. */}
                {draft.lms_enabled && (
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold">Learning notifications (courses)</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      The learner and creator templates live under{' '}
                      <Link to="/learning-settings#notifications" className="underline">
                        Learning → Learning Settings
                      </Link>
                      .
                    </p>
                  </div>
                )}
                <div className="rounded-md border p-3 space-y-2">
                  <h3 className="text-sm font-semibold">Admin email alerts</h3>
                  <Toggle
                    label="Email admins when a payment proof is submitted"
                    description="Sends an email the moment a member submits proof of payment for an ecommerce order (one email per submission, even for a multi-seller cart), so payments waiting for review don't sit unnoticed. Requires the deployment's SMTP settings — ask the platform operator; without them the toggle silently does nothing."
                    value={draft.manual_payment_admin_email.enabled}
                    disabled={!canWrite}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        manual_payment_admin_email: {
                          ...draft.manual_payment_admin_email,
                          enabled: v,
                        },
                      })
                    }
                  />
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Recipients</label>
                    <Textarea
                      rows={2}
                      value={
                        emailRecipientsText ??
                        draft.manual_payment_admin_email.recipients.join('\n')
                      }
                      placeholder={'admin@example.com\nfinance@example.com'}
                      disabled={!canWrite}
                      onChange={(e) => {
                        setEmailRecipientsText(e.target.value);
                        setDraft({
                          ...draft,
                          manual_payment_admin_email: {
                            ...draft.manual_payment_admin_email,
                            recipients: e.target.value
                              .split(/[\n,]+/)
                              .map((s) => s.trim())
                              .filter(Boolean),
                          },
                        });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      One address per line (or comma-separated). Leave empty to email every
                      super-admin&apos;s account email.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="storefront-home" className="lg:col-span-2 scroll-mt-20">
              <CardHeader>
                <CardTitle>Storefront home</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  The storefront home ends in a product grid (titled “Just For You” unless renamed
                  in Layout &amp; Theme) that lists the whole visible catalog. These switches leave
                  products out of that grid when they’re already headlined higher up the page, so a
                  shopper doesn’t see the same item twice. Both are off by default. Neither affects
                  Shop, search, category pages or a direct link — a product left out of the grid is
                  still findable everywhere else.
                </p>
                <Toggle
                  label="Leave Featured Packages out of the product grid"
                  description="The curated products in the Featured Packages strip won't repeat in the grid below it. Does nothing while that section is disabled or its list is empty."
                  value={draft.product_feed_hide_featured_packages}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, product_feed_hide_featured_packages: v })}
                />
                <Toggle
                  label="Leave Featured products out of the product grid"
                  description="Products with the per-product Featured checkbox won't appear in the grid. Careful: Featured otherwise only sorts a product first, so it's often ticked on much of a catalog — check Products before turning this on, or the grid may empty out."
                  value={draft.product_feed_hide_featured_products}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, product_feed_hide_featured_products: v })}
                />
              </CardContent>
            </Card>

            <Card id="system" className="scroll-mt-20">
              <CardHeader>
                <CardTitle>System</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Toggle
                  label="Accounting section"
                  description="Turns on the books: the Accounting area (/accounting) with payables (supplier bills), expenses, cash & bank, consignment, the chart of accounts, journal and financial statements, and automatic posting of sales, payouts and documents to the general ledger from the posting start date."
                  value={draft.accounting_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, accounting_enabled: v })}
                />
                <Toggle
                  label="Consignment (POS)"
                  description="Two-step consignment at the till: consign stock out to a consignee (CN), record their sell-through as a billing (AR), receive payments per billing (PAY), take back unsold stock (RTN). Adds the Consignment area to the cashier app."
                  value={draft.consignment_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, consignment_enabled: v })}
                />
                <Toggle
                  label="Stockist consignment"
                  description="Sell-to-stockist consignment: consign stock out to a stockist branch (SCN) at a frozen price; when that branch's own POS sells it, the company automatically books a receivable (SAR) at the consign-out price — kit codes and PV/points still go to the end buyer through the normal POS sale. Payments (SCPAY) and returns of unsold stock (SRTN) settle the rest. Independent of the POS consignment toggle above."
                  value={draft.stockist_consignment_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, stockist_consignment_enabled: v })}
                />
                <Toggle
                  label="Require a cash/bank account on money movements"
                  description="Supplier payments, expenses and consignment receipts must name the Cash & Bank account the money moved through. Leave off while you are still registering accounts or back-filling history — it is ignored until at least one account exists, so turning it on can never block posting."
                  value={draft.funds_account_required}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, funds_account_required: v })}
                />
                <Toggle
                  label="House VAT on orders"
                  description="Snapshot each house order line's VAT classification (VATable / VAT-exempt / zero-rated, from the product's tax class) at checkout. Prices are already VAT-inclusive — nothing is repriced; this feeds the order Billing documents and the VAT Sales report. Merchant products are unaffected (their VAT is payout withholding)."
                  value={draft.house_vat_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft({ ...draft, house_vat_enabled: v })}
                />
                {draft.house_vat_enabled && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="house-vat-pct">
                      House VAT rate (%)
                    </label>
                    <Input
                      id="house-vat-pct"
                      type="number"
                      min={0}
                      max={100}
                      value={draft.house_vat_default_pct ?? ''}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({ ...draft, house_vat_default_pct: e.target.value })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Snapshotted onto VATable house lines at checkout (PH standard: 12). Changing
                      it never rewrites past sales — only future orders record the new rate.
                    </p>
                  </div>
                )}
                {draft.accounting_enabled && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="gl-posting-start-date">
                      GL posting start date
                      {postingStartPinned && (
                        <Badge className="ml-2" variant="secondary">
                          Pinned
                        </Badge>
                      )}
                    </label>
                    <Input
                      id="gl-posting-start-date"
                      type="date"
                      value={
                        postingStartPinned
                          ? (postingStartQ.data?.postingStart.date ?? '')
                          : (draft.accounting_posting_start_date ?? '')
                      }
                      disabled={!canWrite || postingStartPinned}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          accounting_posting_start_date: e.target.value || null,
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {postingStartPinned
                        ? postingStartQ.data?.postingStart.date
                          ? 'Pinned on the first posting tick and no longer editable — the poster has already skipped everything dated before it. Shown here is the value it actually uses.'
                          : 'Pinned UNBOUNDED on the first posting tick: the poster books all history and no longer reads this field.'
                        : 'The general-ledger poster only books documents dated on or after this date. Set it BEFORE enabling accounting on a tenant with history — it pins permanently on the first tick.'}
                    </p>
                  </div>
                )}
                {draft.accounting_enabled && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="fiscal-year-start-month">
                      Fiscal year starts in
                    </label>
                    <select
                      id="fiscal-year-start-month"
                      className="h-9 w-full max-w-xs rounded-md border bg-background px-2 text-sm"
                      value={draft.accounting_fiscal_year_start_month ?? 1}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          accounting_fiscal_year_start_month: Number(e.target.value),
                        })
                      }
                    >
                      {[
                        'January',
                        'February',
                        'March',
                        'April',
                        'May',
                        'June',
                        'July',
                        'August',
                        'September',
                        'October',
                        'November',
                        'December',
                      ].map((month, i) => (
                        <option key={month} value={i + 1}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Drives the Balance Sheet&apos;s Retained Earnings / current-year Net Income
                      split and the statement date presets. No formal close — calendar year for most
                      PH businesses.
                    </p>
                  </div>
                )}
                <Toggle
                  label="Information site only (no store, no membership)"
                  description={
                    TENANT_HAS_BESPOKE_LANDING
                      ? 'Turns the public site into a plain information site: only this tenant’s own landing pages. The shop, cart, checkout, login, sign-up and the whole member portal are switched off — sign-ups are rejected and members can’t log in. The admin, cashier and merchant apps are unaffected. Turn it back off to restore everything.'
                      : 'Unavailable: this tenant has no landing page of its own, so switching the store off would leave the public site blank.'
                  }
                  value={draft.public_site_info_only}
                  disabled={!canWrite || !TENANT_HAS_BESPOKE_LANDING}
                  onChange={(v) => setDraft({ ...draft, public_site_info_only: v })}
                />
              </CardContent>
            </Card>
          </div>

          {canWrite ? (
            <ConfirmSaveButton
              title="Save Tenant Settings"
              current={featuresQuery.data}
              next={buildPayload()}
              loading={patch.isPending}
              onConfirm={() => patch.mutateAsync(buildPayload())}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Only super_admin can change these settings.
            </p>
          )}
        </div>
      </div>

      {canWrite && <DevResetCard />}
    </div>
  );
}

/** A category key from what the admin typed: lowercase, [a-z0-9_-], at most 30 characters. */
const slugKey = (raw: string) =>
  raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, 30);

type ResetLevel = 'activity' | 'members' | 'factory' | 'slots';

const RESET_LEVELS: { value: ResetLevel; label: string; caption: string }[] = [
  {
    value: 'slots',
    label: 'Slots only',
    caption:
      'Every slot except the root, plus everything they earned: wallets, payouts, comp runs, PV and ranks. Keeps ALL users, orders, memberships & comp configs; kit codes those slots used become unused again.',
  },
  {
    value: 'activity',
    label: 'Activity',
    caption:
      'Wipes transactions only: orders, earnings, wallets, payouts, comp runs. Keeps ALL users, slots, memberships & comp configs.',
  },
  {
    value: 'members',
    label: 'Members',
    caption:
      'Activity + every non-admin user & slot. Keeps memberships & comp configs. (The classic full reset.)',
  },
  {
    value: 'factory',
    label: 'Factory',
    caption:
      'Members + memberships, comp-plan configs, and all branches/stockists + inventory. Keeps super-admin, the root slot (its one tier survives; rename/recode it later), and products (unlinked from kits). Re-seed config with the db:seed CLIs after.',
  },
];

function DevResetCard() {
  const queryClient = useQueryClient();
  // Live boxes leave ALLOW_DEV_RESET unset, so the route hard-403s there. Ask
  // first and render nothing rather than showing a button that can't work.
  const availableQuery = useQuery({
    queryKey: ['dev-reset-available'],
    queryFn: async () => {
      // `scopes` lists what this box opted in to (ALLOW_DEV_RESET=true means
      // all of them); an older API omits it, meaning all.
      const res = await api.get<{ enabled: boolean; scopes?: string[] }>(
        '/admin/dev/reset/available',
      );
      return res.data;
    },
    staleTime: Infinity,
  });
  const scopes = availableQuery.data?.scopes;
  const levels = scopes ? RESET_LEVELS.filter((l) => scopes.includes(l.value)) : RESET_LEVELS;
  const compplanAllowed = !scopes || scopes.includes('compplan');
  const [confirm, setConfirm] = useState('');
  const [level, setLevel] = useState<ResetLevel | null>(null);
  const [compplan, setCompplan] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        keptUserId: string;
        keptSlotId: string | null;
        keptUserEmail: string;
        keptSlotCode: string | null;
        level: ResetLevel | null;
        compplan: boolean;
      }>('/admin/dev/reset', {
        confirm: 'RESET',
        level: level ?? undefined,
        compplan,
      });
      return res.data;
    },
    meta: { successMessage: 'Reset complete.' },
    onSuccess: (data) => {
      const scopes = [data.level, data.compplan ? 'comp-plan configs' : null].filter(Boolean);
      setResult(
        `Reset complete (${scopes.join(' + ')}). Kept ${data.keptUserEmail}${
          data.keptSlotCode ? ` (slot ${data.keptSlotCode})` : ' (no root slot — all slots wiped)'
        }.`,
      );
      setConfirm('');
      setLevel(null);
      setCompplan(false);
      queryClient.invalidateQueries();
    },
    onError: () => {
      setResult('Reset failed. Check server logs.');
    },
  });

  // Need the typed gate AND at least one scope (a level or the ComPlan toggle).
  const ready = confirm === 'RESET' && (level !== null || compplan);

  // Hidden entirely until the box confirms it allows resets (and while the
  // probe is still in flight) — never flash a destructive control on a live
  // deployment.
  if (!availableQuery.data?.enabled) return null;

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">Reset (developer)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Wipe test data at a chosen scope to re-run scenarios from a clean state. Every scope keeps
          your super-admin account, the root slot, roles/permissions, and tenant settings.
        </p>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Reset level
          </p>
          <RadioGroup
            value={level ?? '__none__'}
            onValueChange={(v) => setLevel(v === '__none__' ? null : (v as ResetLevel))}
            className="space-y-2"
          >
            {compplanAllowed && (
              <RadioRow
                value="__none__"
                label="None"
                caption="No level. Only the ComPlan toggle below (if checked) runs."
              />
            )}
            {levels.map((l) => (
              <RadioRow key={l.value} value={l.value} label={l.label} caption={l.caption} />
            ))}
          </RadioGroup>
        </div>

        {compplanAllowed && (
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={compplan}
              onCheckedChange={(v) => setCompplan(v === true)}
              className="mt-1"
            />
            <div className="space-y-0.5">
              <div className="text-sm font-medium">Also wipe ComPlan configs</div>
              <p className="text-xs text-muted-foreground">
                Clears the flat <code className="font-mono">comp_plan_configs</code> table. Per-tier
                comp config inside each membership is NOT touched here; that needs the Factory
                level. Redundant when Factory is selected (Factory already wipes it).
              </p>
            </div>
          </label>
        )}

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            Type <code className="font-mono">RESET</code> to enable the button.
          </p>
          <div className="flex items-center gap-2">
            <Input
              placeholder="RESET"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="max-w-[160px] font-mono"
            />
            <Button
              variant="destructive"
              disabled={!ready || reset.isPending}
              onClick={() => reset.mutate()}
            >
              {reset.isPending ? 'Wiping…' : 'Reset'}
            </Button>
          </div>
        </div>
        {result && <p className="text-xs">{result}</p>}
      </CardContent>
    </Card>
  );
}

function RadioRow({ value, label, caption }: { value: string; label: string; caption: string }) {
  const id = `dev-reset-${value}`;
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
      <RadioGroupItem value={value} id={id} className="mt-1" />
      <div className="space-y-0.5">
        <div className="text-sm font-medium">{label}</div>
        <p className="text-xs text-muted-foreground">{caption}</p>
      </div>
    </label>
  );
}

function LabelInputRow({
  token,
  placeholder,
  value,
  disabled,
  onChange,
}: {
  token: string;
  placeholder: string;
  value: string;
  disabled?: boolean;
  onChange: (raw: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <code
        className="font-mono text-xs text-muted-foreground w-56 shrink-0 truncate"
        title={token}
      >
        {token}
      </code>
      <Input
        className="h-7 text-xs"
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

const BREAKDOWN_ROW_MODES: { value: EarningsBreakdownRowMode; label: string }[] = [
  { value: 'always', label: 'Always show' },
  { value: 'when_earned', label: 'Show when earned' },
  { value: 'hidden', label: 'Hidden' },
];

/**
 * Row editor for the member "Earnings Breakdown" panel. Each row is one bucket
 * from the shared registry (a comp plan, or a kind-level sub-bucket like the
 * mentor rider on Monthly Direct) with a display label + visibility mode;
 * array order = display order. Resolves the starting list with the SAME
 * `resolveEarningsBreakdownRows` the member route uses, so tenants still on
 * the legacy `earnings_breakdown_order` see their arrangement pre-loaded.
 * Buckets the tenant disabled are hidden (a sub-bucket hides with its parent
 * plan); `payout` isn't in the registry. Buckets not yet configured append in
 * default order as "Show when earned", so enabling a new plan later surfaces
 * it at the end without an admin touch. Every change emits the FULL
 * materialized rows array. Labels are NOT stored in the row config — they
 * write through `onLabelChange` into the shared ledger label maps.
 */
function EarningsBreakdownRowsEditor({
  rows,
  legacyOrder,
  disabledPlans,
  planLabels,
  kindLabels,
  disabled,
  onChange,
  onLabelChange,
}: {
  rows: EarningsBreakdownRow[];
  legacyOrder: string[];
  disabledPlans: string[];
  planLabels: Record<string, string>;
  kindLabels: Record<string, string>;
  disabled?: boolean;
  onChange: (next: EarningsBreakdownRow[]) => void;
  onLabelChange: (key: string, raw: string) => void;
}) {
  const availableDefs = EARNINGS_BUCKETS.filter((b) => !disabledPlans.includes(b.plan));
  const availableKeys = new Set(availableDefs.map((b) => b.key));
  const configured = resolveEarningsBreakdownRows({
    earnings_breakdown_rows: rows,
    earnings_breakdown_order: legacyOrder,
  }).filter((r) => availableKeys.has(r.key));
  const configuredKeys = new Set(configured.map((r) => r.key));
  const ordered: EarningsBreakdownRow[] = [
    ...configured,
    ...availableDefs
      .filter((b) => !configuredKeys.has(b.key))
      .map((b): EarningsBreakdownRow => ({ key: b.key, mode: 'when_earned' })),
  ];

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= ordered.length) return;
    const next = [...ordered];
    const tmp = next[idx]!;
    next[idx] = next[j]!;
    next[j] = tmp;
    onChange(next);
  };
  const setMode = (idx: number, mode: EarningsBreakdownRowMode) =>
    onChange(ordered.map((r, i) => (i === idx ? { ...r, mode } : r)));

  if (ordered.length === 0) {
    return <p className="text-xs text-muted-foreground">No earning plans are enabled.</p>;
  }

  return (
    <div className="space-y-1.5">
      {ordered.map((row, idx) => {
        const sub = EARNINGS_SUB_BUCKETS.find((b) => b.key === row.key);
        const labelValue = sub?.kind ? (kindLabels[sub.kind] ?? '') : (planLabels[row.key] ?? '');
        return (
          <div
            key={row.key}
            className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5"
          >
            <span className="w-5 text-center text-xs tabular-nums text-muted-foreground">
              {idx + 1}
            </span>
            <div className="flex-1 min-w-40">
              <Input
                className="h-7 text-xs"
                placeholder={earningsBreakdownRowLabel(row.key)}
                disabled={disabled}
                value={labelValue}
                onChange={(e) => onLabelChange(row.key, e.target.value)}
              />
              {row.key === 'monthly_direct_mentor_bonus' && (
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  mentor rider on Monthly Direct
                </p>
              )}
            </div>
            <Select
              value={row.mode}
              disabled={disabled}
              onValueChange={(v) => setMode(idx, v as EarningsBreakdownRowMode)}
            >
              <SelectTrigger className="h-7 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BREAKDOWN_ROW_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <code className="font-mono text-[10px] text-muted-foreground">{row.key}</code>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-6 w-6"
              disabled={disabled || idx === 0}
              title="Move up"
              onClick={() => move(idx, -1)}
            >
              ↑
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-6 w-6"
              disabled={disabled || idx === ordered.length - 1}
              title="Move down"
              onClick={() => move(idx, 1)}
            >
              ↓
            </Button>
          </div>
        );
      })}
    </div>
  );
}

/** The Select's value for "this bracket gives no gift check" (Radix needs a non-empty value). */
const NO_PROGRAM = '__none__';

const TOPUP_BONUS_KINDS: { value: WalletTopupBracket['bonusKind']; label: string }[] = [
  { value: 'pct_wallet', label: '% to Cash wallet' },
  { value: 'pct_gc', label: '% to GC' },
  { value: 'fixed_wallet', label: 'Fixed Cash' },
  { value: 'fixed_gc', label: 'Fixed GC' },
];

/**
 * Typed editor for wallet top-up bonus brackets (no raw JSON). Each row is one
 * bracket: amount range (min / max-or-∞), a bonus kind, and a value (a percent
 * for pct_*, a fixed amount for fixed_*). ALL brackets whose range contains the
 * amount apply — e.g. a "% to Wallet" row + a "% to GC" row over the same range
 * both credit. A live preview runs the SAME `resolveTopupBonuses` the server
 * uses against a sample amount.
 */
function WalletTopupBracketsEditor({
  value,
  disabled,
  programs,
  onChange,
}: {
  value: WalletTopupBracket[];
  disabled?: boolean;
  /** Gift check programs earned by a top-up; null while gift checks are off. */
  programs: Array<{ id: string; name: string }> | null;
  onChange: (next: WalletTopupBracket[]) => void;
}) {
  const [sample, setSample] = useState('1000');
  const patch = (i: number, p: Partial<WalletTopupBracket>) =>
    onChange(value.map((b, idx) => (idx === i ? { ...b, ...p } : b)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([
      ...value,
      {
        id: crypto.randomUUID(),
        minAmount: '0',
        maxAmount: null,
        bonusKind: 'pct_wallet',
        value: '1',
      },
    ]);
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
    onChange(next);
  };

  let preview: ReturnType<typeof resolveTopupBonuses> = [];
  let previewPrograms: string[] = [];
  try {
    preview = resolveTopupBonuses(value, new Decimal(sample || '0'));
    previewPrograms = programs
      ? resolveTopupVoucherPrograms(value, new Decimal(sample || '0')).map(
          (id) => programs.find((p) => p.id === id)?.name ?? 'a gift check',
        )
      : [];
  } catch {
    preview = [];
    previewPrograms = [];
  }

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No bonus brackets. Top-ups credit the principal only.
        </p>
      ) : (
        <div className="space-y-1.5">
          {value.map((b, i) => (
            <div
              key={b.id}
              className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5"
            >
              <span className="w-4 text-center text-xs tabular-nums text-muted-foreground">
                {i + 1}
              </span>
              <Input
                type="number"
                min={0}
                step="any"
                className="h-7 w-24 text-xs"
                placeholder="Min"
                disabled={disabled}
                value={b.minAmount}
                onChange={(e) => patch(i, { minAmount: e.target.value })}
              />
              <span className="text-xs text-muted-foreground">–</span>
              <Input
                type="number"
                min={0}
                step="any"
                className="h-7 w-24 text-xs"
                placeholder="∞ (no max)"
                disabled={disabled}
                value={b.maxAmount ?? ''}
                onChange={(e) =>
                  patch(i, { maxAmount: e.target.value === '' ? null : e.target.value })
                }
              />
              <Select
                value={b.bonusKind}
                disabled={disabled}
                onValueChange={(v) => patch(i, { bonusKind: v as WalletTopupBracket['bonusKind'] })}
              >
                <SelectTrigger className="h-7 w-40 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOPUP_BONUS_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                min={0}
                step="any"
                className="h-7 w-24 text-xs"
                placeholder={b.bonusKind.startsWith('pct') ? '%' : 'amount'}
                disabled={disabled}
                value={b.value}
                onChange={(e) => patch(i, { value: e.target.value })}
              />
              {programs && (
                <Select
                  value={b.voucherProgramId ?? NO_PROGRAM}
                  disabled={disabled}
                  onValueChange={(v) => patch(i, { voucherProgramId: v === NO_PROGRAM ? null : v })}
                >
                  <SelectTrigger className="h-7 w-48 text-xs" aria-label="Gift check">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PROGRAM}>No gift check</SelectItem>
                    {programs.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        + {p.name}
                      </SelectItem>
                    ))}
                    {/* A saved program that is no longer listed (deleted, switched off). */}
                    {b.voucherProgramId && !programs.some((p) => p.id === b.voucherProgramId) && (
                      <SelectItem value={b.voucherProgramId}>(unavailable program)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
              <div className="ml-auto flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-6 w-6"
                  disabled={disabled || i === 0}
                  title="Move up"
                  onClick={() => move(i, -1)}
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-6 w-6"
                  disabled={disabled || i === value.length - 1}
                  title="Move down"
                  onClick={() => move(i, 1)}
                >
                  ↓
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-6 w-6"
                  disabled={disabled}
                  title="Remove"
                  onClick={() => remove(i)}
                >
                  ×
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={add}>
          + Add bracket
        </Button>
        <span className="text-xs text-muted-foreground">Preview a</span>
        <Input
          type="number"
          min={0}
          step="any"
          className="h-7 w-28 text-xs"
          value={sample}
          onChange={(e) => setSample(e.target.value)}
        />
        <span className="text-xs text-muted-foreground">
          top-up →{' '}
          {preview.length > 0 || previewPrograms.length > 0
            ? [
                ...preview.map((p) => `+${p.bonus.toFixed(2)} ${p.bonusCurrency.toUpperCase()}`),
                ...previewPrograms.map((name) => `+ ${name}`),
              ].join(' · ')
            : 'no bonus'}
        </span>
      </div>
    </div>
  );
}

/** Turn a label into a stable, unique field id (lowercase slug). */
function slugifyFieldId(label: string, taken: Set<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 48) || 'field';
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}_${n++}`;
  return id;
}

/**
 * Typed editor for admin-defined signup fields. Adds/removes rows (label, type,
 * required); each field's `id` is slugged from its label at creation and stays
 * stable so stored values don't orphan when the label is later edited.
 */
function RegistrationFieldsEditor({
  value,
  disabled,
  onChange,
}: {
  value: RegistrationField[];
  disabled?: boolean;
  onChange: (v: RegistrationField[]) => void;
}) {
  const update = (i: number, patch: Partial<RegistrationField>) =>
    onChange(value.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i: number) =>
    onChange(value.filter((_, idx) => idx !== i).map((f, idx) => ({ ...f, order: idx })));
  const add = () => {
    const taken = new Set(value.map((f) => f.id));
    onChange([
      ...value,
      {
        id: slugifyFieldId('field', taken),
        label: '',
        type: 'text',
        required: false,
        order: value.length,
      },
    ]);
  };

  return (
    <div className="space-y-2">
      {value.length === 0 && <p className="text-xs text-muted-foreground">No custom fields yet.</p>}
      {value.map((f, i) => (
        <div key={f.id} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
          <Input
            className="h-8 w-48"
            placeholder="Field label (e.g. Company Name)"
            value={f.label}
            disabled={disabled}
            onChange={(e) => {
              const label = e.target.value;
              // Keep the id in sync with the label until the field has been saved
              // with a meaningful id; once a non-placeholder id exists, leave it.
              const taken = new Set(value.filter((_, idx) => idx !== i).map((x) => x.id));
              update(i, { label, id: slugifyFieldId(label || 'field', taken) });
            }}
          />
          <Select
            value={f.type}
            disabled={disabled}
            onValueChange={(v) => update(i, { type: v as RegistrationField['type'] })}
          >
            <SelectTrigger className="h-8 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="file">File upload</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox
              checked={f.required}
              disabled={disabled}
              onCheckedChange={(v) => update(i, { required: v === true })}
            />
            Required
          </label>
          {!disabled && (
            <Button size="sm" variant="ghost" onClick={() => remove(i)}>
              Remove
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button size="sm" variant="outline" onClick={add}>
          Add field
        </Button>
      )}
    </div>
  );
}

/**
 * Which membership tier's prices a buyer with NO active slot pays. "Off" (the
 * default) means they pay SRP, as they always have. The picker only lists
 * ACTIVE tiers; if the saved tier was archived after being picked it is shown
 * as an explicit "not applied" row so the setting never blanks itself silently
 * — the server likewise stops matching it and the buyer falls back to SRP.
 */
const NON_ACTIVATED_OFF = '__off__';

function NonActivatedPricingPicker({
  value,
  memberships,
  disabled,
  onChange,
}: {
  value: string | null;
  memberships: Membership[];
  disabled?: boolean;
  onChange: (v: string | null) => void;
}) {
  const missing = value != null && !memberships.some((m) => m.id === value);
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">Non-activated member pricing</label>
      <Select
        value={value ?? NON_ACTIVATED_OFF}
        disabled={disabled}
        onValueChange={(v) => onChange(v === NON_ACTIVATED_OFF ? null : v)}
      >
        <SelectTrigger className="h-9 max-w-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NON_ACTIVATED_OFF}>Off — pay full price (default)</SelectItem>
          {memberships.map((m) => (
            <SelectItem key={m.id} value={m.id}>
              {m.name}
            </SelectItem>
          ))}
          {missing && <SelectItem value={value!}>Archived tier — not applied</SelectItem>}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        A member who has not activated a slot yet normally pays the full price. Pick a tier here and
        they get that tier's product prices instead — online and at the counter — so they can enjoy
        a member price before activating. <strong>PV is never discounted</strong> and no rank
        pricing applies. This does <strong>not</strong> trigger when a cashier simply deselects an
        activated member's slot; that buyer still pays full price.
      </p>
    </div>
  );
}

/**
 * One admin-editable event template. Module scope, NOT defined inside
 * `SettingsPage`: a component declared in a render body gets a fresh identity
 * every keystroke, React remounts it, and the title/body inputs lose focus
 * mid-typing. `draft`/`setDraft`/`canWrite` therefore arrive as props.
 */
function DefaultSponsorPicker({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}) {
  const resolvedQ = useQuery({
    queryKey: ['admin-user', value],
    queryFn: () => getAdminUser(value!),
    enabled: !!value,
    staleTime: 60_000,
  });
  const selected: AdminUserSearchResult | null = value
    ? resolvedQ.data
      ? {
          id: resolvedQ.data.id,
          fullName: resolvedQ.data.fullName,
          email: resolvedQ.data.email,
          referralCode: resolvedQ.data.referralCode,
        }
      : { id: value, fullName: 'Loading…', email: '', referralCode: null }
    : null;
  return (
    <MemberPicker
      selected={selected}
      disabled={disabled}
      onChange={(u) => onChange(u?.id ?? null)}
    />
  );
}

/** Derive a stable slug key from a label (lowercase, underscores). */
function slugifyKycKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

function KycRequirementsEditor({
  value,
  disabled,
  onChange,
}: {
  value: KycRequirementDef[];
  disabled?: boolean;
  onChange: (v: KycRequirementDef[]) => void;
}) {
  const update = (i: number, patch: Partial<KycRequirementDef>) =>
    onChange(value.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const add = () => onChange([...value, { key: '', label: '', required: true }]);

  // A row's key auto-fills from its label until the admin edits the key directly.
  const setLabel = (i: number, label: string) => {
    const cur = value[i]!;
    const autoKey = cur.key === '' || cur.key === slugifyKycKey(cur.label);
    update(i, { label, ...(autoKey ? { key: slugifyKycKey(label) } : {}) });
  };

  const keys = value.map((it) => it.key);
  const hasDupKey = keys.some((k, i) => k !== '' && keys.indexOf(k) !== i);

  return (
    <div className="space-y-2">
      {value.map((it, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border p-3">
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Label</span>
            <Input
              className="h-8 w-48"
              placeholder="Government ID — front"
              value={it.label}
              disabled={disabled}
              onChange={(e) => setLabel(i, e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Key</span>
            <Input
              className="h-8 w-40 font-mono"
              placeholder="gov_id_front"
              value={it.key}
              disabled={disabled}
              onChange={(e) => update(i, { key: slugifyKycKey(e.target.value) })}
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Help text (optional)</span>
            <Input
              className="h-8 w-56"
              placeholder="Shown under the upload slot"
              value={it.description ?? ''}
              disabled={disabled}
              onChange={(e) => update(i, { description: e.target.value || undefined })}
            />
          </div>
          <label className="flex h-8 items-center gap-1.5 text-sm">
            <Checkbox
              checked={it.required}
              disabled={disabled}
              onCheckedChange={(v) => update(i, { required: v === true })}
            />
            Required
          </label>
          {!disabled && (
            <div className="ml-auto flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>
                ↑
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => move(i, 1)}
                disabled={i === value.length - 1}
              >
                ↓
              </Button>
              <Button variant="ghost" size="sm" onClick={() => remove(i)}>
                Remove
              </Button>
            </div>
          )}
        </div>
      ))}
      {hasDupKey && <p className="text-xs text-destructive">Keys must be unique.</p>}
      {!disabled && (
        <Button size="sm" variant="outline" onClick={add}>
          Add requirement
        </Button>
      )}
    </div>
  );
}

/**
 * What an answer actually costs you this month, so the price per answer can be
 * set above it rather than guessed. Uses the same month-to-date figures as the
 * usage panel; silent until there's real usage to average.
 */
function CreditPricingHint({ usage }: { usage?: AssistantUsageSummary }) {
  if (!usage || usage.exchangeCount === 0) {
    return (
      <p className="flex-1 text-[11px] text-muted-foreground">
        Once members start chatting, the average cost per answer shows here so you can price above
        it.
      </p>
    );
  }
  const avgUsd = Number(usage.monthUsd) / usage.exchangeCount;
  return (
    <p className="flex-1 text-[11px] text-muted-foreground">
      This month an answer has cost you about{' '}
      <span className="font-medium text-foreground">${avgUsd.toFixed(4)}</span> on average (
      {usage.exchangeCount} answers). Price above that — and remember the free answers above are on
      you, not the member.
    </p>
  );
}

/**
 * Month-to-date AI Assistant usage: spend against the configured ceiling,
 * where it went (per model and per persona), and how the answers landed.
 */
function AssistantUsagePanel({ usage }: { usage: AssistantUsageSummary }) {
  const spent = Number(usage.monthUsd);
  const budget = usage.budget.monthlyUsd ? Number(usage.budget.monthlyUsd) : null;
  const pct = budget && budget > 0 ? Math.min(100, (spent / budget) * 100) : null;
  const { quality } = usage;

  return (
    <div className="space-y-3 border-t pt-3 text-xs">
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium">
            ${spent.toFixed(2)} est. this month · {usage.exchangeCount} messages
          </span>
          <span className="text-muted-foreground">
            {budget ? `of $${budget.toFixed(2)} budget` : 'no monthly budget set'}
          </span>
        </div>
        {pct !== null && (
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full', pct >= 80 ? 'bg-destructive' : 'bg-primary')}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      {usage.byPersona.length > 0 && (
        <div className="space-y-1">
          <p className="text-muted-foreground">Where it went</p>
          <table className="w-full">
            <tbody>
              {usage.byPersona.map((p) => (
                <tr key={p.personaId ?? 'unknown'} className="border-t first:border-t-0">
                  <td className="py-1">
                    {p.icon} {p.name}
                  </td>
                  <td className="py-1 text-right text-muted-foreground">{p.exchanges} msgs</td>
                  <td className="py-1 text-right tabular-nums">${Number(p.costUsd).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {usage.byModel.length > 1 && (
            <p className="text-muted-foreground">
              By model:{' '}
              {usage.byModel
                .map((m) => `${m.model.replace('claude-', '')} $${Number(m.costUsd).toFixed(2)}`)
                .join(' · ')}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        <span>👍 {quality.feedbackUp}</span>
        <span>👎 {quality.feedbackDown}</span>
        <span>declined {quality.refusals}</span>
        <span>cut short {quality.truncated}</span>
        <span>stopped {quality.stopped}</span>
        {usage.webSearchRequests > 0 && <span>web searches {usage.webSearchRequests}</span>}
      </div>
    </div>
  );
}
